/**
 * Browser AHP relay connection runtime.
 *
 * Owns one `AhpClient` + Web PubSub transport pair for a single Mission Control
 * mediated connection to an environment. It connects, discovers the session to
 * observe, applies the subscription snapshot once, then fans every live action
 * out to the caller. Transport drops trigger a token-refreshed re-establish;
 * the snapshot re-applied on reconnect recovers any missed actions (the
 * consumer's projector is idempotent).
 *
 * Dispatches user turns and tool-call confirmation responses for an observed session.
 */

import {
  AhpClient,
  RpcError,
  type AhpTransport,
  type JsonRpcMessage,
  type Subscription,
} from '@microsoft/agent-host-protocol/client'
import type {
  AgentSelection,
  ActionEnvelope,
  ActionType,
  ChangesetState,
  ChatState,
  CompletionItem,
  CompletionItemKind,
  ContentEncoding,
  InitializeResult,
  MessageAttachment,
  ModelSelection,
  PendingMessageKind,
  ReconnectResult,
  ResourceReadResult,
  ResourceWriteParams,
  RootAction,
  RootState,
  SessionAction,
  SessionActiveClient,
  SessionState,
  SessionToolAuthenticationRequest,
  StateAction,
  ToolCallResult,
  ToolDefinition,
  ToolInput,
  TelemetryCapabilities,
  URI,
} from '@microsoft/agent-host-protocol'
import {AhpErrorCodes, JsonRpcErrorCodes, rootReducer, sessionReducer} from '@microsoft/agent-host-protocol'
import {ResourceWriteError, toResourceWriteError} from './resource-write'
import {WpsTransport, type WebSocketFactory} from './webpubsub/wps-transport'
import {parseGroupName} from './webpubsub/groups'
import {
  buildWpsUrl,
  connectToEnvironment,
  reconnectToEnvironment,
  type MCConnectResponse,
  type MCFetchOptions,
  type MCFetcher,
} from './mission-control/mission-control-environment-client'
import {
  createAwpsAuthenticationContext,
  AUTH_REQUIRED_CODE,
  authRequiredResources,
  defaultResourceTokenProvider,
  satisfyResources,
  withFallbackResourceTokenProvider,
  type AwpsAuthenticationContext,
  type ResourceTokenProvider,
} from './protected-resource-auth'
import {
  createSessionWithAuth,
  sealedGitHubTokenProvider,
  toWorkingDirectoryUri,
  type CreateSessionParams,
} from './session-host/create-session'
import {
  ProjectProvisioningError,
  resolveProjectWorkingDirectory,
  type ProvisioningStatus,
  type ResolvedProject,
} from './session-host/resolve-project'
import {CLIENT_SUPPORTED_AHP_VERSIONS} from './protocol-support'
import {buildExplainErrorSections} from './explain-error-prompt'
import {buildPlaywrightLocalhostSections} from './playwright-localhost-prompt'
import {buildSessionScopeSections, type RelaySessionScope} from './session-scope-prompt'
import {mergeSystemMessageSections} from './system-message-sections'
import {normalizeSessionAction} from './protocol-compatibility'
import {inlineToolInput} from './tool-input'
import {buildInputCompletedAction, type BuildInputCompletedActionParams} from './ahp-input-actions'
import {isPassiveHandshakeResult} from './passive-connection'
import {createClientTurnId} from './turn-identity'
import {buildPendingMessageRemovedAction, buildPendingMessageSetAction} from './pending-message-actions'
import {
  decideLiveness,
  keepAlivePingIntervalMs,
  keepAlivePingsEnabled,
  LIVENESS_PROBE_TIMEOUT_MS,
  readKeepAliveTimeoutMs,
} from './relay-liveness'
import {RelayTerminalState} from './relay-terminal-state'
import {RelayConnectionError, relayFailureDisposition, relayFailureFromError, type RelayFailure} from './relay-failure'
import {formatError} from './format-error'
import type {RuleFile} from './instruction-plugin-source'
import {InstructionPluginRuntime} from './instruction-plugin-runtime'
import {buildUserMessage, type ContributedInstructionsMetadata} from './pending-message-actions'

const ROOT_CHANNEL = 'ahp-root://' as const
/** Fallback clone-auth resource, used only when the daemon advertises no protected resources of its own. */
const GITHUB_API_RESOURCE = 'https://api.github.com'
const SESSION_CONFIG_TARGET_WORKTREE = 'worktree'
const ACTION_TURN_STARTED = 'chat/turnStarted' as ActionType.ChatTurnStarted
const ACTION_ACTIVE_CLIENT_SET = 'session/activeClientSet' as ActionType.SessionActiveClientSet
const ACTION_TOOL_CALL_START = 'chat/toolCallStart' as ActionType.ChatToolCallStart
const ACTION_TOOL_CALL_DELTA = 'chat/toolCallDelta' as ActionType.ChatToolCallDelta
const ACTION_TOOL_CALL_READY = 'chat/toolCallReady' as ActionType.ChatToolCallReady
const ACTION_TOOL_CALL_CONFIRMED = 'chat/toolCallConfirmed' as ActionType.ChatToolCallConfirmed
const ACTION_TOOL_CALL_COMPLETE = 'chat/toolCallComplete' as ActionType.ChatToolCallComplete
const ACTION_TURN_CANCELLED = 'chat/turnCancelled' as ActionType.ChatTurnCancelled
const ACTION_TURN_COMPLETE = 'chat/turnComplete' as ActionType.ChatTurnComplete
const ACTION_TURN_ERROR = 'chat/error' as ActionType.ChatError
const ACTION_SESSION_META_CHANGED = 'session/metaChanged' as ActionType.SessionMetaChanged
const ACTION_ROOT_ACTIVE_SESSIONS_CHANGED = 'root/activeSessionsChanged' as ActionType.RootActiveSessionsChanged
const SESSION_INPUT_KIND_TOOL_AUTHENTICATION = 'toolAuthentication'
/**
 * Session `_meta` key carrying the latest failed model call. copilotd reports
 * some failures (e.g. `model_not_supported`) only here while still marking the
 * turn `complete`, so without reading it the failed turn renders as blank.
 */
const META_KEY_MODEL_CALL_FAILURE = 'copilot.modelCallFailure'
const ACTION_SESSION_ACTIVE_CLIENT_SET = 'session/activeClientSet' as ActionType.SessionActiveClientSet
const ACTION_SESSION_ACTIVE_CLIENT_REMOVED = 'session/activeClientRemoved' as ActionType.SessionActiveClientRemoved
const ACTION_SESSION_TITLE_CHANGED = 'session/titleChanged' as ActionType.SessionTitleChanged
const ACTION_SESSION_CONFIG_CHANGED = 'session/configChanged' as ActionType.SessionConfigChanged
const RECONNECT_BASE_DELAY_MS = 1_000
const RECONNECT_MAX_DELAY_MS = 30_000
const SESSION_DISPATCH_ACK_TIMEOUT_MS = 15_000
const MAX_AUTH_RETRIES = 1
// JSON-RPC code the daemon returns when a subscribed session does not exist.
const SESSION_NOT_FOUND_CODE = -32008

function classifySessionSubscriptionError(error: unknown): unknown {
  if (!(error instanceof RpcError) || error.code !== SESSION_NOT_FOUND_CODE) return error
  return new RelayConnectionError(
    error.message,
    {kind: 'rpc', code: error.code, subtype: 'sessionNotFound'},
    {cause: error},
  )
}

/** The AHP channel for a session, observed over the relay. */
export function sessionChannel(sessionId: string): URI {
  return `ahp-session:/${sessionId}`
}

export type RelayTelemetrySignal = 'logs' | 'traces' | 'metrics'

export interface RelayTelemetryEvent {
  signal: RelayTelemetrySignal
  channel: URI
  payload: Record<string, unknown>
}

export interface RelayTelemetryObserver {
  onEvent(event: RelayTelemetryEvent): void
  onError?(message: string): void
}

/**
 * The legacy default chat channel used by persisted pre-0.9 event histories.
 * Live connections must use the opaque URI advertised in session state.
 */
export function chatChannelFor(sessionChannelUri: URI): URI {
  return `${sessionChannelUri}/chat`
}

export type RelayStatus = 'idle' | 'connecting' | 'connected' | 'reconnecting' | 'closed' | 'error'

export type RelayConnectionProgressPhase =
  | 'contacting-environment'
  | 'waking-environment'
  | 'opening-transport'
  | 'initializing-connection'
  | 'authenticating'
  | 'restoring-session'
  | 'restoring-conversation'
  | 'retrying'
  | 'connected'

export interface RelayConnectionProgress {
  phase: RelayConnectionProgressPhase
  retryAttempt?: number
}

export interface RelayLogger {
  info(message: string): void
  error(message: string): void
}

const noopLogger: RelayLogger = {info: () => {}, error: () => {}}

export type AhpClientFactory = (transport: AhpTransport) => AhpClient

const defaultAhpClientFactory: AhpClientFactory = transport => new AhpClient(transport)

// Options for the client-driven `createSession` the relay issues on first
// establish, for sessions MC bound but never created on the daemon.
export interface RelayCreateSessionOptions {
  /** Working directory for the session, required when the daemon has no workspace roots. */
  workingDirectory?: string
  /** Model the new session is created with. Omit to use the daemon default. */
  model?: ModelSelection
  /**
   * Opt the new session into the cloud session store search tool. Merged into the
   * daemon `createSession` params `config` as `config.enableSessionStore`, where
   * copilot-host reads it.
   */
  enableSessionStore?: boolean
  /**
   * GitHub scope this session is bound to. Appended to the host's
   * `environment_context` system-prompt section so the agent knows which owner
   * (and repository, when there is one) its credentials can reach.
   */
  sessionScope?: RelaySessionScope
  /**
   * The daemon runs the managed Playwright MCP server, which is restricted to
   * loopback. Appended to the host's `runtime_instructions` system-prompt
   * section so the agent does not attempt external sites. Omit for a
   * user-managed host, which runs the user's own MCP configuration.
   */
  managedPlaywrightMcp?: boolean
  /**
   * The session was started from "Explain error" on a failed Actions job.
   * Appended to the host's `runtime_instructions` system-prompt section so the
   * agent goes straight to the job's logs and stops once the cause is clear.
   */
  explainError?: boolean
  /**
   * Resolve this repository to a host project (catalogue match or clone) and bind
   * the session to a git worktree of it; takes precedence over `workingDirectory`.
   */
  project?: RelayProjectProvisioning
  /**
   * Active-client customization published at session creation time. Merged
   * with the tool definitions contributed via
   * {@link RelayConnectionRuntimeOptions.clientTools} into a single
   * `SessionActiveClient` (`clientId` is set by the runtime); the merged
   * publication is re-asserted on every reconnect.
   */
  activeClient?: (clientId: string) => SessionActiveClient
  /**
   * Client-only: subscribe (attach) first and create the daemon session only on
   * `-32008`. For a hard-navigated observe (managed sandbox) whose session may
   * already exist. Not sent to the daemon.
   */
  attachOrCreate?: boolean
}

/** Repository-to-project provisioning request for a worktree-backed session. */
export interface RelayProjectProvisioning {
  nwo: string
  cloneUrl: string
  /**
   * Base branch for the worktree; the host auto-generates the actual working
   * branch. Falls back to the repo's default branch, then the project's main
   * checkout.
   */
  baseBranch?: string
}

/** A tool call the host has routed to this client for execution. */
export interface ClientToolCall {
  toolCallId: string
  turnId: string
  toolName: string
  /** Raw tool input the host streamed for this call (a JSON string), if any. */
  toolInput?: string
}

/**
 * Internal bookkeeping for a routed client tool call between `toolCallStart`
 * and `toolCallReady`. `deltas` accumulates the `chat/toolCallDelta` chunks the
 * host streams for the arguments, so we can fall back to them when
 * `toolCallReady` omits the consolidated `toolInput`.
 */
interface OwnedToolCall {
  toolCallId: string
  turnId: string
  toolName: string
  deltas: string
  /** Set once we've approved a call the host left in `pending-confirmation`. */
  confirmed?: boolean
  /** `toolInput` seen on the confirmation ready, used if a later ready omits it. */
  readyInput?: string
}

/**
 * A tool this client contributes to the observed session. The runtime includes
 * the {@link definition} in the `SessionActiveClient` published via
 * `session/activeClientSet` and — when the host routes a matching call to this
 * client — runs {@link fulfil} and reports the result via
 * `chat/toolCallComplete`.
 *
 * Tools can be registered or unregistered dynamically at any time via
 * {@link RelayConnectionRuntime.registerTool} and
 * {@link RelayConnectionRuntime.unregisterTool}. Each mutation re-publishes
 * `session/activeClientSet` so the host is always aware of the current tool set.
 */
export interface ClientTool {
  definition: ToolDefinition
  /**
   * Execute the tool and return its result. Omit for render-only tools (the
   * timeline renders from the call's `toolInput`); the runtime then completes
   * them with a bare success so the turn can proceed.
   */
  fulfil?: (call: ClientToolCall) => ToolCallResult | Promise<ToolCallResult>
}

export interface RelayConnectionRuntimeOptions {
  environmentId: string
  /** Mission Control session id; the relay observes this session's channel. */
  sessionId: string
  /** Channel URI to observe. Defaults to `ahp-session:/<sessionId>`. */
  channel?: URI
  /** Receives the chat snapshot each time it is (re)established. */
  onSnapshot: (state: ChatState) => void
  /** Receives every live action in `serverSeq` order. */
  onAction: (env: ActionEnvelope) => void
  /** Receives the folded session catalog after snapshots and session actions. */
  onSessionState?: (state: SessionState) => void
  onStatus?: (status: RelayStatus, error?: string, failure?: RelayFailure) => void
  /** Observational connection milestones for user-facing progress. */
  onConnectionProgress?: (progress: RelayConnectionProgress) => void
  /** Receives passivity changes for this AHP connection. Passive means read-only. */
  onPassiveChange?: (passive: boolean) => void
  onProvisioning?: (status: ProvisioningStatus) => void
  /** Receives host/environment info discovered during the AHP `initialize` handshake. */
  onHostInfo?: (info: RelayHostInfo) => void
  /** Receives a `_meta` model-call failure (copilotd reports it without a `chat/error`), tagged with its turn id. */
  onModelCallFailure?: (failure: RelayModelCallFailure, turnId: string) => void
  /** Performs Mission Control requests. Required unless both `mcConnect` and `mcReconnect` are supplied. */
  fetcher?: MCFetcher
  /** Mints WPS credentials. Defaults to the Mission Control connect endpoint. */
  mcConnect?: (options?: MCFetchOptions) => Promise<MCConnectResponse>
  /** Refreshes WPS credentials for reconnect. Defaults to the reconnect endpoint. */
  mcReconnect?: (clientId: string, options?: MCFetchOptions) => Promise<MCConnectResponse>
  // When set, issue `createSession` once on first establish before subscribing.
  createSession?: RelayCreateSessionOptions
  webSocketFactory?: WebSocketFactory
  ahpClientFactory?: AhpClientFactory
  /** Resolves per-resource tokens when the host requires authentication. */
  resourceTokenProvider?: ResourceTokenProvider
  /** Initial client tools to register. Can be extended later via registerTool/unregisterTool. */
  clientTools?: ClientTool[]
  logger?: RelayLogger
}

export interface RelayChatObserver {
  onSnapshot: (state: ChatState) => void
  onAction: (env: ActionEnvelope) => void
  onError?: (error: string) => void
}

interface ObservedChat {
  observer: RelayChatObserver
  subscription?: Subscription
}

function isChatState(state: unknown): state is ChatState {
  return !!state && typeof state === 'object' && Array.isArray((state as {turns?: unknown}).turns)
}

export interface RelayChangesetObserver {
  onSnapshot: (state: ChangesetState) => void
  onAction: (env: ActionEnvelope) => void
  onError?: (error: string) => void
}

interface ObservedChangeset {
  observer: RelayChangesetObserver
  subscription?: Subscription
}

function isChangesetState(state: unknown): state is ChangesetState {
  return !!state && typeof state === 'object' && Array.isArray((state as {files?: unknown}).files)
}

function isRootState(state: unknown): state is RootState {
  return !!state && typeof state === 'object' && Array.isArray((state as {agents?: unknown}).agents)
}

function isSessionState(state: unknown): state is SessionState {
  return !!state && typeof state === 'object' && Array.isArray((state as {chats?: unknown}).chats)
}

/** A model-call failure lifted from the session `_meta` side-channel, normalized for any consumer. */
export interface RelayModelCallFailure {
  model?: string
  message: string
  code?: string
  statusCode?: number
}

export interface RelayHostInfo {
  serverName?: string
  serverVersion?: string
  serverTitle?: string
  protocolVersion?: string
  defaultDirectory?: string
  hostName?: string
  /** Active (non-disposed) sessions in the host's `listSessions` catalogue. */
  activeSessions?: number
}

interface PendingSessionDispatch {
  resolve: () => void
  reject: (error: Error) => void
  timer: ReturnType<typeof setTimeout>
}

function readHostName(root: RootState | undefined): string | undefined {
  const copilotNs = root?.config?.values?.['copilot']
  if (!copilotNs || typeof copilotNs !== 'object') return undefined
  return nonEmptyString((copilotNs as {hostName?: unknown}).hostName)
}

function nonEmptyString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined
}

/**
 * copilotd's `errorMessage` is often a JSON string like `{"message":"…"}`; lift
 * the nested message, else fall back to the raw string / a generic message.
 */
function readModelFailureMessage(raw: Record<string, unknown>): string {
  const errorMessage = nonEmptyString(raw.errorMessage)
  if (!errorMessage) return 'The model failed to respond.'
  try {
    const parsed = JSON.parse(errorMessage) as {message?: unknown}
    const nested = nonEmptyString(parsed.message)
    if (nested) return nested
  } catch {
    // Not JSON — use the raw string.
  }
  return errorMessage
}

/** Normalize the `copilot.modelCallFailure` `_meta` payload; `undefined` when the key is absent or malformed. */
export function normalizeModelCallFailure(
  meta: Record<string, unknown> | undefined,
): RelayModelCallFailure | undefined {
  const raw = meta?.[META_KEY_MODEL_CALL_FAILURE]
  if (!raw || typeof raw !== 'object') return undefined
  const record = raw as Record<string, unknown>
  const model = nonEmptyString(record.model)
  const code = nonEmptyString(record.errorCode)
  const statusCode = typeof record.statusCode === 'number' ? record.statusCode : undefined
  return {
    message: readModelFailureMessage(record),
    ...(model ? {model} : {}),
    ...(code ? {code} : {}),
    ...(statusCode !== undefined ? {statusCode} : {}),
  }
}

/** Read the routing-hint chat URI a session points new input at. */
export function defaultChatChannel(state: unknown): URI | undefined {
  if (!state || typeof state !== 'object') return undefined
  const session = state as {defaultChat?: unknown; chats?: Array<{resource?: unknown}>}
  if (typeof session.defaultChat === 'string' && session.defaultChat) return session.defaultChat
  const firstChat = Array.isArray(session.chats) ? session.chats[0]?.resource : undefined
  return typeof firstChat === 'string' ? firstChat : undefined
}

export class RelayConnectionRuntime {
  private readonly logger: RelayLogger
  private readonly ahpClientFactory: AhpClientFactory

  private client: AhpClient | null = null
  private transport: AhpTransport | null = null
  private mcClientId: string | undefined
  private lastSeenServerSeq = 0
  private rootState: RootState | undefined
  private sessionState: SessionState | undefined
  private resourceTokenProvider: ResourceTokenProvider | undefined
  private authentication: AwpsAuthenticationContext | undefined
  private readonly toolAuthenticationInFlight = new Set<string>()
  /** Chat channel resolved from the session's `defaultChat`; the conversation
   * (turns, tool calls) is observed and dispatched here as of AHP 0.4.0. */
  private chatChannel: URI | undefined
  private readonly observedChats = new Map<URI, ObservedChat>()
  private readonly observedChangesets = new Map<URI, ObservedChangeset>()
  private telemetryCapabilities: TelemetryCapabilities | undefined
  private readonly telemetryObservers = new Set<RelayTelemetryObserver>()
  private readonly telemetryChannels = new Set<URI>()
  private telemetryStartInFlight: Promise<void> | null = null

  /** Id of the streaming turn, tracked so {@link cancelTurn} can target it. */
  private activeTurnId: string | undefined

  /**
   * Id of the most recently started turn. Unlike {@link activeTurnId} it is NOT
   * cleared when the turn ends, so a model-call failure arriving later on the
   * session `_meta` side-channel can still be attributed to its turn.
   */
  private lastTurnId: string | undefined
  private hostInfoBase: RelayHostInfo | undefined
  private activeSessions: number | undefined
  private sessionResources: Set<URI> | undefined

  /**
   * `apiCallId`s of already-surfaced model-call failures. The host never clears
   * the latest failure from `_meta`, so it rides along on every later
   * `session/metaChanged`; dedupe so each is surfaced once.
   */
  private readonly seenModelFailureIds = new Set<string>()

  /** Bumped on every connect attempt / teardown to invalidate stale async work. */
  private connectGen = 0
  private connectAbortController: AbortController | undefined
  /** Pending exponential-backoff reconnect timer, cleared on teardown/dispose so a released relay stops reconnecting. */
  private reconnectTimer: ReturnType<typeof setTimeout> | undefined
  private disposed = false
  private reconnectAttempts = 0
  private status: RelayStatus = 'idle'
  /**
   * True while an establish is in flight. A refresh arriving meanwhile is
   * skipped rather than spawning a redundant `/connect`, which collapses a burst
   * of consumers mounting on the same task into a single connection.
   */
  private establishInFlight = false
  /**
   * `connectGen` value that already has a reconnect scheduled. Guards against
   * both the chat and session-keepalive subscriptions racing to schedule a
   * reconnect for the same failed connection: they share a transport, so a
   * network drop terminates both, and without this dedup we would
   * double-increment `reconnectAttempts` and mis-set the exponential backoff.
   */
  private reconnectScheduledForGen: number | undefined
  /**
   * Whether the current `mcClientId` ever reached a healthy `connected` state.
   *
   * Mission Control delivers the daemon spawn (which makes the daemon join this
   * client's `to-host` group and answer `initialize`) ONLY on `/connect`, not on
   * `/reconnect`. So a client whose spawn was dropped or that connected before the
   * daemon's data plane was ready never gets answered, and reconnecting with the
   * same client id loops on the `initialize` timeout forever. We only take the
   * cheap `/reconnect` path once a connection has actually become healthy;
   * otherwise (and after any failed (re)establish) we escalate to a fresh
   * `/connect` so a new client id + new spawn is published.
   */
  private clientEverConnected = false
  private readonly terminalState: RelayTerminalState

  /**
   * Timestamp of the last frame this client sent toward the host, used to tell
   * whether the relay lane may have been reclaimed while the tab sat idle. Only
   * outbound traffic counts: the reapers measure this client's idleness, so
   * inbound host streaming does not keep the lane alive.
   */
  private lastOutboundAtMs: number | undefined
  /**
   * Timestamp of the last response to a client request. A successful handshake
   * or ping proves the host still receives this client's frames; a transport ack
   * for a fire-and-forget dispatch does not.
   */
  private lastConfirmedAtMs: number | undefined

  /** Keep-alive window advertised by the host at handshake, when it reports one. */
  private keepAliveTimeoutMs: number | undefined

  /** Pending idle keep-alive `ping`, re-armed on every outbound frame. */
  private keepAliveTimer: ReturnType<typeof setTimeout> | undefined

  private activeClientPublished = false
  private readonly instructionPlugin = new InstructionPluginRuntime()

  // Guards `createSession` to the first establish; reconnects skip it.
  private sessionCreated = false

  /**
   * Tool call ids the host has attributed to this client (from `toolCallStart`
   * with a matching client contributor) and that we still owe a
   * `chat/toolCallComplete` for. Cleared on teardown so a reconnect starts
   * clean.
   */
  private readonly ownedToolCalls = new Map<string, OwnedToolCall>()

  /**
   * `chat/toolCallReady` inputs that arrived before their matching
   * `chat/toolCallStart`. copilotd can publish the two out of order (they travel
   * on separate async paths — see {@link AhpChatStore}'s equivalent buffer), and
   * ownership is only known once `toolCallStart` names our client contributor.
   * Buffered by tool-call id and replayed when an owned start registers the
   * call, so an out-of-order ready never leaves the call (and its turn) stalled
   * waiting for a `chat/toolCallComplete` we would otherwise never send. Cleared
   * on teardown so a reconnect starts clean.
   */
  private readonly pendingClientToolReady = new Map<string, {toolInput?: string; needsConfirmation: boolean}>()
  private readonly pendingSessionDispatches = new Map<number, PendingSessionDispatch>()

  /** Mutable list of client tools. Mutated via registerTool/unregisterTool. */
  private readonly tools: ClientTool[] = []

  private readonly options: RelayConnectionRuntimeOptions
  private passive = false
  private passiveKnown = false
  private connectionProgress: RelayConnectionProgress | undefined

  constructor(options: RelayConnectionRuntimeOptions) {
    this.options = options
    this.logger = options.logger ?? noopLogger
    this.ahpClientFactory = options.ahpClientFactory ?? defaultAhpClientFactory
    this.terminalState = new RelayTerminalState({
      logger: this.logger,
      onProvisioning: options.onProvisioning,
      teardownClient: () => this.teardownClient(),
      setStatus: (status, error, failure) => this.setStatus(status, error, failure),
      setClientEverConnected: clientEverConnected => {
        this.clientEverConnected = clientEverConnected
      },
    })
    if (options.clientTools) {
      this.tools.push(...options.clientTools)
    }
  }

  isTerminated(): boolean {
    return this.terminalState.isTerminated()
  }

  private observedChannel(): URI {
    return this.options.channel ?? sessionChannel(this.options.sessionId)
  }

  async connect(): Promise<void> {
    if (this.disposed) throw new Error('relay connection runtime is disposed')
    const gen = ++this.connectGen
    this.establishInFlight = true
    const signal = this.beginConnectAttempt()
    this.setStatus('connecting')
    try {
      await this.establish(gen, false, signal)
    } catch (err) {
      if (gen !== this.connectGen || this.disposed) return
      if (await this.terminalState.handleTerminalEstablishError(err)) return
      const failure = relayFailureFromError(err)
      if (relayFailureDisposition(failure).automaticRetry === 'ignore') return
      // The (re)establish failed before becoming healthy; force the next
      // attempt to take the full `/connect` path (new client id + spawn).
      this.clientEverConnected = false
      const msg = formatError(err)
      this.logger.error(`relay connect failed: ${msg}`)
      this.setStatus('error', msg, failure)
      this.scheduleReconnect(gen)
    } finally {
      this.settleEstablish(gen)
      this.finishConnectAttempt(signal)
    }
  }

  observeChat(channel: URI, observer: RelayChatObserver): () => void {
    if (this.disposed) throw new Error('relay connection runtime is disposed')
    if (channel === ROOT_CHANNEL || channel === this.observedChannel() || channel === this.chatChannel) {
      throw new Error(`relay already observes chat channel '${channel}'`)
    }
    if (this.observedChats.has(channel) || this.observedChangesets.has(channel)) {
      throw new Error(`relay already observes chat channel '${channel}'`)
    }

    const observed: ObservedChat = {observer}
    this.observedChats.set(channel, observed)
    if (this.status === 'connected') void this.startObservedChat(channel, observed, this.connectGen)

    return () => {
      if (this.observedChats.get(channel) !== observed) return
      this.observedChats.delete(channel)
      const client = this.client
      if (client) {
        void client.unsubscribe(channel)
      } else {
        void observed.subscription?.close()
      }
    }
  }

  observeChangeset(channel: URI, observer: RelayChangesetObserver): () => void {
    if (this.disposed) throw new Error('relay connection runtime is disposed')
    if (channel === ROOT_CHANNEL || channel === this.observedChannel() || channel === this.chatChannel) {
      throw new Error(`relay already observes changeset channel '${channel}'`)
    }
    if (this.observedChangesets.has(channel) || this.observedChats.has(channel)) {
      throw new Error(`relay already observes changeset channel '${channel}'`)
    }

    const observed: ObservedChangeset = {observer}
    this.observedChangesets.set(channel, observed)
    if (this.status === 'connected') void this.startObservedChangeset(channel, observed, this.connectGen)

    return () => {
      if (this.observedChangesets.get(channel) !== observed) return
      this.observedChangesets.delete(channel)
      const client = this.client
      if (client) {
        void client.unsubscribe(channel)
      } else {
        void observed.subscription?.close()
      }
    }
  }

  async readResource(uri: URI): Promise<ResourceReadResult> {
    if (this.disposed) throw new Error('relay connection runtime is disposed')
    if (!this.client || this.status !== 'connected') throw new Error('relay connection is not connected')
    return this.client.resourceRead({uri, encoding: 'utf-8' as ContentEncoding})
  }

  async subscribeTelemetry(observer: RelayTelemetryObserver): Promise<() => void> {
    if (this.disposed) throw new Error('relay connection runtime is disposed')
    this.telemetryObservers.add(observer)
    try {
      // Subscribe now if the handshake has completed on the current client. This closes the
      // race where the observer is added between establish()'s telemetry step and setStatus,
      // during which `status` is still `connecting`/`reconnecting` but the client is live.
      // Otherwise leave it to the next establish() cycle, which checks observer count.
      const client = this.client
      if (client && this.telemetryCapabilities !== undefined) {
        await this.startTelemetrySubscriptions(client)
      }
    } catch (error) {
      this.telemetryObservers.delete(observer)
      throw error
    }

    return () => {
      if (!this.telemetryObservers.delete(observer) || this.telemetryObservers.size > 0) return
      const channels = [...this.telemetryChannels]
      this.telemetryChannels.clear()
      const client = this.client
      if (client) {
        for (const channel of channels) void client.unsubscribe(channel)
      }
    }
  }

  /** Clear the in-flight guard, unless a newer attempt has superseded this one. */
  private settleEstablish(gen: number): void {
    if (gen !== this.connectGen) return
    this.establishInFlight = false
  }

  private dispatchOnChat(action: StateAction): void {
    if (this.passive) throw new Error('relay connection is read-only')
    const client = this.client
    if (!client) throw new Error('relay is not connected')
    const chat = this.chatChannel
    if (!chat) throw new Error('relay chat channel is not resolved')
    this.markOutbound()
    client.dispatch(chat, action)
  }

  private dispatchOnSession(action: StateAction): void {
    if (this.passive) throw new Error('relay connection is read-only')
    const client = this.client
    if (!client) throw new Error('relay is not connected')
    this.markOutbound()
    client.dispatch(this.observedChannel(), action)
  }

  private dispatchOnSessionAcknowledged(action: StateAction): Promise<void> {
    if (this.passive) throw new Error('relay connection is read-only')
    const client = this.client
    if (!client) throw new Error('relay is not connected')
    if (!this.mcClientId) throw new Error('relay client id is not resolved')
    this.markOutbound()
    const {clientSeq} = client.dispatch(this.observedChannel(), action)
    return new Promise<void>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pendingSessionDispatches.delete(clientSeq)
        reject(new Error('timed out waiting for the host to accept the session update'))
      }, SESSION_DISPATCH_ACK_TIMEOUT_MS)
      this.pendingSessionDispatches.set(clientSeq, {resolve, reject, timer})
    })
  }

  private settlePendingSessionDispatch(envelope: ActionEnvelope, sourceChannel: URI): void {
    const origin = envelope.origin
    if (
      sourceChannel !== this.observedChannel() ||
      !origin ||
      origin.clientId !== this.mcClientId ||
      origin.clientSeq === undefined
    ) {
      return
    }
    const pending = this.pendingSessionDispatches.get(origin.clientSeq)
    if (!pending) return
    this.pendingSessionDispatches.delete(origin.clientSeq)
    clearTimeout(pending.timer)
    if (envelope.rejectionReason) {
      pending.reject(new Error(`session update rejected by host: ${envelope.rejectionReason}`))
    } else {
      pending.resolve()
    }
  }

  private rejectPendingSessionDispatches(error: Error): void {
    for (const pending of this.pendingSessionDispatches.values()) {
      clearTimeout(pending.timer)
      pending.reject(error)
    }
    this.pendingSessionDispatches.clear()
  }

  private markOutbound(): void {
    this.lastOutboundAtMs = Date.now()
    this.armKeepAlivePing()
  }

  private markConfirmed(): void {
    const now = Date.now()
    this.lastOutboundAtMs = now
    this.lastConfirmedAtMs = now
    this.armKeepAlivePing()
  }

  /**
   * (Re)arm the idle keep-alive `ping`. The host slides its reclaim deadline
   * forward on any inbound frame, so measuring from the last outbound frame
   * means an actively used lane never pings at all.
   */
  private armKeepAlivePing(): void {
    this.stopKeepAlivePing()
    if (this.disposed || !this.client || this.terminalState.isTerminated()) return
    if (!keepAlivePingsEnabled()) return
    this.keepAliveTimer = setTimeout(() => {
      this.keepAliveTimer = undefined
      void this.sendKeepAlivePing()
    }, keepAlivePingIntervalMs(this.keepAliveTimeoutMs))
  }

  private stopKeepAlivePing(): void {
    if (this.keepAliveTimer === undefined) return
    clearTimeout(this.keepAliveTimer)
    this.keepAliveTimer = undefined
  }

  /**
   * An unanswered keep-alive is deliberately not recovered here: reconnecting
   * would wake stopped compute for a tab nobody may be watching. Stop pinging
   * and leave the lane dead until `ensureLive` re-establishes it for a real
   * dispatch.
   *
   * `probeLane` re-arms the timer through `markOutbound` before the ping
   * resolves, so the failure path has to disarm it again. A probe can outlive
   * its own connection though, and by then the timer belongs to a lane
   * established while this ping hung, so a stale failure has to leave it alone.
   */
  private async sendKeepAlivePing(): Promise<void> {
    if (this.disposed || !this.client || this.terminalState.isTerminated()) return
    const gen = this.connectGen
    if (await this.probeLane()) return
    if (gen !== this.connectGen || this.disposed) return
    this.stopKeepAlivePing()
    this.logger.info('relay keep-alive ping went unanswered; leaving recovery to the next dispatch')
  }

  /**
   * Confirm the relay lane still reaches the host, reconnecting when it does not.
   *
   * Callers should await this immediately before dispatching after the tab may
   * have been idle. It sends nothing while idle, so the sandbox behind the
   * session is still free to suspend; the cost is paid only when the user acts.
   *
   * Returns `true` when re-establishment is in progress or was required, which
   * can take tens of seconds if the sandbox has to be woken. `onReconnect` runs
   * before that wait begins so callers can surface the resume state immediately.
   */
  async ensureLive(onReconnect?: () => void): Promise<boolean> {
    if (this.disposed || this.passive || this.terminalState.isTerminated()) return false
    // An establish already in flight will itself produce a fresh lane, but the
    // immediate-dispatch caller must still wait for it rather than treating the
    // current connection as ready.
    if (this.establishInFlight) {
      onReconnect?.()
      return true
    }
    if (!this.client) return false
    const decision = decideLiveness({
      lastOutboundAtMs: this.lastOutboundAtMs,
      lastConfirmedAtMs: this.lastConfirmedAtMs,
      nowMs: Date.now(),
      ...(this.keepAliveTimeoutMs !== undefined ? {keepAliveTimeoutMs: this.keepAliveTimeoutMs} : {}),
    })
    if (decision === 'skip') return false
    if (decision === 'probe' && (await this.probeLane())) return false
    onReconnect?.()
    await this.reconnectDeadLane()
    return true
  }

  /**
   * Round-trip a protocol-level `ping`. Unlike `dispatchAction` (a
   * fire-and-forget notification whose transport ack proves nothing), `ping` is
   * a request, so a missing response is itself the signal that the lane is gone.
   */
  private async probeLane(): Promise<boolean> {
    const client = this.client
    if (!client) return false
    let timer: ReturnType<typeof setTimeout> | undefined
    try {
      this.markOutbound()
      await Promise.race([
        client.ping(),
        new Promise<never>((_resolve, reject) => {
          timer = setTimeout(() => reject(new Error('relay liveness probe timed out')), LIVENESS_PROBE_TIMEOUT_MS)
        }),
      ])
      this.markConfirmed()
      return true
    } catch (err) {
      this.logger.info(`relay liveness probe failed: ${formatError(err)}`)
      return false
    } finally {
      if (timer !== undefined) clearTimeout(timer)
    }
  }

  /**
   * Re-establish a lane the host is no longer listening on.
   *
   * This must take the full `/connect` path. Mission Control's `/reconnect`
   * only mints a fresh Web PubSub token: it does not wake suspended compute and
   * does not re-publish the daemon spawn, so reconnecting a dead lane with it
   * would hand back a valid token pointing at a host that never joins
   * `to-host`. Clearing `clientEverConnected` is what forces `establish` to
   * mint a new client id instead.
   */
  private async reconnectDeadLane(): Promise<void> {
    this.logger.info('relay lane is unreachable; reconnecting via a fresh Mission Control connect')
    this.clientEverConnected = false
    this.lastOutboundAtMs = undefined
    this.lastConfirmedAtMs = undefined
    await this.refresh()
  }

  /** Publish the tools and customizations this web client contributes to the observed session. */
  publishActiveClient(activeClient: SessionActiveClient): void {
    this.dispatchOnSession({
      type: ACTION_SESSION_ACTIVE_CLIENT_SET,
      activeClient,
    })
    this.activeClientPublished = true
  }

  /** Replace the custom-instruction rules contributed by this web client. */
  setInstructionRules(rules: RuleFile[]): void {
    if (!this.instructionPlugin.setRules(rules)) return

    const client = this.client
    if (!client) return
    this.instructionPlugin.registerResourceHandlers(client, (method, uri) =>
      this.logger.info(`relay: inbound ${method} for ${uri}`),
    )
    this.advertiseActiveClient(client, this.instructionPlugin.hasPublishedCustomization())
  }

  /** Complete a running client-provided tool call on the observed chat channel. */
  completeToolCall({turnId, toolCallId, result}: {turnId: string; toolCallId: string; result: ToolCallResult}): void {
    this.dispatchOnChat({
      type: ACTION_TOOL_CALL_COMPLETE,
      turnId,
      toolCallId,
      result,
    })
  }

  /** The Mission Control/AHP client id for the current live connection. */
  getClientId(): string {
    if (!this.mcClientId) throw new Error('relay client id is not resolved')
    return this.mcClientId
  }

  /** Rename the observed AHP session. Requires a live connection. */
  renameSession(title: string): void {
    this.dispatchOnSession({
      type: ACTION_SESSION_TITLE_CHANGED,
      title,
    })
  }

  /** Toggle per-session auto-approval ("allow all") on the host via the
   * `extensions/setSessionApproveAll` vendor RPC. Awaited so failures surface. */
  async setSessionApproveAll(enabled: boolean): Promise<void> {
    if (this.passive) throw new Error('relay connection is read-only')
    const client = this.client
    if (!client) throw new Error('relay is not connected')
    // Vendor `extensions/*` method the typed CommandMap doesn't cover; one contained cast.
    const send = client.request as unknown as (method: string, params: unknown) => Promise<unknown>
    await send.call(client, 'extensions/setSessionApproveAll', {channel: this.observedChannel(), enabled})
  }

  /**
   * Create a directory on the host filesystem, creating parents as needed.
   *
   * `resourceWrite` fails with `NotFound` when the parent directory is missing, so the
   * upload path calls this once before the first write of a session.
   */
  async createHostDirectory(uri: string): Promise<void> {
    const client = this.requireWritableClient()
    try {
      await client.request('resourceMkdir', {channel: ROOT_CHANNEL, uri})
    } catch (error) {
      throw toResourceWriteError(error)
    }
  }

  /**
   * Write bytes to a file on the host filesystem.
   *
   * A thin pass-through to the typed `resourceWrite` command: chunking, collision
   * renaming and retry policy all live in the caller, because only the caller knows
   * how the bytes were split. Errors are normalized to {@link ResourceWriteError} so
   * callers branch on a stable kind rather than raw JSON-RPC codes.
   */
  async writeHostResource(params: {
    uri: string
    data: string
    encoding: 'base64' | 'utf-8'
    contentType?: string
    createOnly?: boolean
    mode?: 'truncate' | 'append' | 'insert'
    position?: number
  }): Promise<void> {
    const client = this.requireWritableClient()
    try {
      await client.request('resourceWrite', {
        channel: ROOT_CHANNEL,
        uri: params.uri,
        data: params.data,
        encoding: params.encoding as ResourceWriteParams['encoding'],
        contentType: params.contentType,
        createOnly: params.createOnly,
        mode: params.mode as ResourceWriteParams['mode'],
        position: params.position,
      })
    } catch (error) {
      throw toResourceWriteError(error)
    }
  }

  /**
   * Stats a written resource. `resourceWrite` returns an empty result, so this is the
   * only way to learn the canonical path the agent will see and to confirm how many
   * bytes actually landed.
   */
  async resolveHostResource(uri: string): Promise<{uri: string; size?: number}> {
    const client = this.requireWritableClient()
    try {
      const result = await client.request('resourceResolve', {channel: ROOT_CHANNEL, uri})
      return {uri: result.uri, size: result.size}
    } catch (error) {
      throw toResourceWriteError(error)
    }
  }

  /** Best-effort removal of a partially written upload. Never throws. */
  async deleteHostResource(uri: string): Promise<void> {
    const client = this.client
    if (this.passive || !client) return
    try {
      await client.request('resourceDelete', {channel: ROOT_CHANNEL, uri})
    } catch (error) {
      this.logger.info(`relay: failed to clean up ${uri}: ${String(error)}`)
    }
  }

  /** Whether this relay can currently issue filesystem writes to the host. */
  canWriteHostResources(): boolean {
    return !this.passive && this.client !== undefined
  }

  private requireWritableClient(): AhpClient {
    if (this.passive) throw new ResourceWriteError('denied', 'relay connection is read-only')
    const client = this.client
    if (!client) throw new ResourceWriteError('failed', 'relay is not connected')
    return client
  }

  /** Change the selected approval mode through the host's advertised session config. */ setSessionApprovalMode(
    mode: import('./permission-mode').SessionApprovalMode,
  ): Promise<void> {
    return this.dispatchOnSessionAcknowledged({
      type: ACTION_SESSION_CONFIG_CHANGED,
      config: {approvalMode: mode},
    })
  }

  /**
   * Set the per-session agent mode (interactive/plan/autopilot) by dispatching the
   * typed, client-dispatchable `session/configChanged` action. `mode` is a
   * session-mutable property of the host's advertised session-config schema, so the
   * host's reducer merges it into `state.config.values`. Merge semantics mean this
   * leaves the session's other config properties untouched.
   */
  setSessionMode(mode: string): Promise<void> {
    return this.dispatchOnSessionAcknowledged({
      type: ACTION_SESSION_CONFIG_CHANGED,
      config: {mode},
    })
  }

  /** Dispatch a new user turn on the observed session's chat channel. Requires a live connection. */
  dispatchTurn(
    text: string,
    attachments?: MessageAttachment[],
    model?: ModelSelection,
    agent?: AgentSelection,
    contributedInstructions?: ContributedInstructionsMetadata,
  ): string {
    const turnId = createClientTurnId()
    const startedAt = new Date().toISOString()
    const action: StateAction = {
      type: ACTION_TURN_STARTED,
      turnId,
      startedAt,
      message: buildUserMessage(text, attachments, model, agent, contributedInstructions),
    }
    // Track optimistically so a Stop click lands before the daemon echoes the
    // `chat/turnStarted` action back through the subscription. `trackActiveTurn`
    // re-sets the same id on the echo, and terminal actions clear it.
    this.activeTurnId = turnId
    this.dispatchOnChat(action)
    return turnId
  }

  /** Queue a future user message on the observed session's chat channel. Requires a live connection. */
  queueMessage(
    text: string,
    attachments?: MessageAttachment[],
    model?: ModelSelection,
    agent?: AgentSelection,
    contributedInstructions?: ContributedInstructionsMetadata,
  ): string {
    const action = buildPendingMessageSetAction(
      'queued' as PendingMessageKind.Queued,
      text,
      attachments,
      model,
      undefined,
      agent,
      contributedInstructions,
    )
    this.dispatchOnChat(action)
    return action.id
  }

  /** Steer the active turn with a user message. Requires a live connection. */
  steerMessage(
    text: string,
    attachments?: MessageAttachment[],
    model?: ModelSelection,
    id?: string,
    agent?: AgentSelection,
    contributedInstructions?: ContributedInstructionsMetadata,
  ): string {
    const action = buildPendingMessageSetAction(
      'steering' as PendingMessageKind.Steering,
      text,
      attachments,
      model,
      id,
      agent,
      contributedInstructions,
    )
    this.dispatchOnChat(action)
    return action.id
  }

  /** Remove a queued user message from the observed session's chat channel. Requires a live connection. */
  removeQueuedMessage(id: string): void {
    this.dispatchOnChat(buildPendingMessageRemovedAction('queued' as PendingMessageKind.Queued, id))
  }

  /** Request host-authorized completion items for the current chat input. */
  async completions(text: string, offset: number): Promise<CompletionItem[]> {
    const client = this.client
    const chat = this.chatChannel
    if (!client || !chat) return []
    const result = await client.completions({
      kind: 'userMessage' as CompletionItemKind,
      channel: chat,
      text,
      offset,
    })
    return result.items
  }

  /**
   * Cancel the active turn (or the given `turnId`) by dispatching
   * `chat/turnCancelled`. Returns `false` when there is nothing to cancel or the
   * relay is not currently connected (a Stop click during reconnect is a no-op).
   */
  cancelTurn(turnId?: string): boolean {
    if (this.passive) return false
    const client = this.client
    const chat = this.chatChannel
    const target = turnId ?? this.activeTurnId
    if (!client || !chat || !target) return false
    const action: StateAction = {
      type: ACTION_TURN_CANCELLED,
      turnId: target,
      // This client may have attached after the producer started the turn, so it
      // cannot reconstruct producer-measured duration. Zero is an explicit
      // "unknown from this client" value rather than a cross-clock subtraction.
      duration: 0,
    }
    this.markOutbound()
    client.dispatch(chat, action)
    return true
  }

  /**
   * Force the runtime to drop its current connection and re-establish, which
   * causes the next `subscribe` to return a fresh snapshot. Useful when a new
   * consumer mounts on top of an already-active runtime whose cached snapshot
   * may not reflect the current daemon state (e.g. responses streamed in
   * after the original consumer was torn down).
   *
   * A refresh tears down the live client and re-subscribes to pull a fresh
   * snapshot. When an establish is already in flight (connecting/reconnecting),
   * that attempt will itself deliver a fresh snapshot when it lands, so this is a
   * no-op — which collapses a burst of consumers mounting on the same task in one
   * commit into a single connection.
   */
  async refresh(): Promise<void> {
    if (this.disposed || this.establishInFlight) return
    const gen = ++this.connectGen
    this.establishInFlight = true
    this.setStatus('connecting')
    await this.teardownClient()
    if (gen !== this.connectGen || this.disposed) {
      this.settleEstablish(gen)
      return
    }
    const signal = this.beginConnectAttempt()
    try {
      await this.establish(gen, true, signal)
    } catch (err) {
      if (gen !== this.connectGen || this.disposed) return
      if (await this.terminalState.handleTerminalEstablishError(err)) return
      const failure = relayFailureFromError(err)
      if (relayFailureDisposition(failure).automaticRetry === 'ignore') return
      this.clientEverConnected = false
      const msg = formatError(err)
      this.logger.error(`relay refresh failed: ${msg}`)
      this.setStatus('error', msg, failure)
      this.scheduleReconnect(gen)
    } finally {
      this.settleEstablish(gen)
      this.finishConnectAttempt(signal)
    }
  }

  /** Approve or deny a pending tool call on the observed session's chat channel. Requires a live connection. */
  confirmToolCall({
    approved,
    toolCallId,
    turnId,
    resultConfirmation,
    selectedOptionId,
    forSession,
    chat: chatOverride,
  }: {
    approved: boolean
    toolCallId: string
    turnId: string
    resultConfirmation?: boolean
    selectedOptionId?: string
    forSession?: boolean
    /** Answer on this chat instead of the observed one (for session-level confirmations). */
    chat?: URI
  }): void {
    if (this.passive) throw new Error('relay connection is read-only')
    const client = this.client
    if (!client) throw new Error('relay is not connected')
    const chat = chatOverride ?? this.chatChannel
    if (!chat) throw new Error('relay chat channel is not resolved')

    const action = resultConfirmation
      ? {
          type: 'chat/toolCallResultConfirmed',
          turnId,
          toolCallId,
          approved,
        }
      : approved
        ? {
            type: 'chat/toolCallConfirmed',
            turnId,
            toolCallId,
            approved: true,
            // The host reads scope from `confirmed` alone and ignores
            // `selectedOptionId`, so a session-scoped choice must be sent as
            // `setting` or it silently degrades to a one-time approval.
            confirmed: forSession ? 'setting' : 'user-action',
            ...(selectedOptionId ? {selectedOptionId} : {}),
          }
        : {
            type: 'chat/toolCallConfirmed',
            turnId,
            toolCallId,
            approved: false,
            reason: 'denied',
            reasonMessage: 'Denied by user',
            ...(selectedOptionId ? {selectedOptionId} : {}),
          }
    this.markOutbound()
    client.dispatch(chat, action as StateAction)
  }

  /**
   * Accept, decline, or cancel a pending input request (`ask_user`/elicitation)
   * on the observed session's chat channel. Requires a live connection.
   */
  completeInput(params: BuildInputCompletedActionParams): void {
    if (this.passive) throw new Error('relay connection is read-only')
    const client = this.client
    if (!client) throw new Error('relay is not connected')
    const chat = this.chatChannel
    if (!chat) throw new Error('relay chat channel is not resolved')
    this.markOutbound()
    client.dispatch(chat, buildInputCompletedAction(params))
  }

  /**
   * Register a client tool dynamically. If a tool with the same name already
   * exists it is replaced (upsert). Automatically announces the updated tool
   * set to the host via `session/activeClientSet` if connected.
   */
  registerTool(tool: ClientTool): void {
    this.upsertTool(tool)
    if (this.client) this.advertiseActiveClient(this.client)
  }

  /**
   * Claim the active-client role with a complete, acknowledged tool catalog.
   * Unlike registerTool(), this is a correctness boundary: callers may dispatch
   * user input only after the host accepts the claim.
   */
  async claimActiveClient(tools: readonly ClientTool[]): Promise<void> {
    if (this.passive) throw new Error('relay connection is read-only')
    if (!this.client) throw new Error('relay is not connected')
    if (!this.mcClientId) throw new Error('relay client id is not resolved')

    for (const tool of tools) this.upsertTool(tool)
    const activeClient = this.buildActiveClient(true)
    if (!activeClient) throw new Error('relay active client could not be built')

    try {
      await this.dispatchOnSessionAcknowledged({
        type: ACTION_ACTIVE_CLIENT_SET,
        activeClient,
      })
      this.activeClientPublished = true
    } catch (error) {
      this.logger.error(`relay: claiming active client failed: ${formatError(error)}`)
      throw error
    }
  }

  private upsertTool(tool: ClientTool): void {
    const idx = this.tools.findIndex(t => t.definition.name === tool.definition.name)
    if (idx >= 0) {
      this.tools[idx] = tool
    } else {
      this.tools.push(tool)
    }
  }

  /**
   * Unregister a client tool by name. Automatically announces the updated
   * tool set to the host via `session/activeClientSet` if connected.
   *
   * TODO: removing the final tool does not publish an empty `tools: []` to the
   * host (advertiseActiveClient no-ops when buildActiveClient returns undefined).
   * This is acceptable today because existing client tools are never removed at
   * runtime.
   */
  unregisterTool(name: string): void {
    const idx = this.tools.findIndex(t => t.definition.name === name)
    if (idx >= 0) {
      this.tools.splice(idx, 1)
      if (this.client) this.advertiseActiveClient(this.client)
    }
  }

  async dispose(): Promise<void> {
    if (this.disposed) return
    this.disposed = true
    this.connectGen++
    this.abortConnectAttempt()
    await this.teardown()
    this.observedChats.clear()
    this.observedChangesets.clear()
    this.telemetryObservers.clear()
    this.setStatus('closed')
  }

  private async establish(gen: number, isReconnect: boolean, signal: AbortSignal): Promise<void> {
    // Only reuse the cheap `/reconnect` path (which does NOT re-publish the
    // daemon spawn) when this client id already reached a healthy connection.
    // Otherwise mint a fresh client via `/connect` so a new spawn is published.
    const reconnectClientId = isReconnect && this.clientEverConnected ? this.mcClientId : undefined
    this.setConnectionProgress({phase: 'contacting-environment'})
    const mc =
      reconnectClientId !== undefined
        ? await (this.options.mcReconnect?.(reconnectClientId, {signal}) ??
            this.mcReconnectDefault(reconnectClientId, signal))
        : await (this.options.mcConnect?.({signal}) ?? this.mcConnectDefault(signal))
    if (gen !== this.connectGen || this.disposed) return
    if (reconnectClientId !== undefined && mc.client_id !== reconnectClientId) {
      throw new Error(`Mission Control reconnect changed client_id from '${reconnectClientId}' to '${mc.client_id}'`)
    }
    this.mcClientId = mc.client_id

    this.setConnectionProgress({phase: 'opening-transport'})
    const transport = await this.openTransport(mc)
    if (gen !== this.connectGen || this.disposed) {
      await safeClose(transport)
      return
    }
    this.transport = transport

    this.setConnectionProgress({phase: 'initializing-connection'})
    const client = this.ahpClientFactory(transport)
    this.client = client
    this.instructionPlugin.registerResourceHandlers(client, (method, uri) =>
      this.logger.info(`relay: inbound ${method} for ${uri}`),
    )
    client.connect()

    const reconnectChannels =
      reconnectClientId !== undefined
        ? [
            ...new Set([
              ROOT_CHANNEL,
              this.observedChannel(),
              ...(this.chatChannel ? [this.chatChannel] : []),
              ...this.observedChats.keys(),
              ...this.observedChangesets.keys(),
            ]),
          ]
        : [ROOT_CHANNEL]
    const localSubscriptions = new Map<URI, Subscription>(
      reconnectChannels.map(channel => [channel, client.attachSubscription(channel)]),
    )
    let resumedConnection = false
    let handshake: (InitializeResult | ReconnectResult) & {_meta?: Record<string, unknown>}
    if (reconnectClientId !== undefined) {
      try {
        handshake = await client.reconnect({
          clientId: mc.client_id,
          lastSeenServerSeq: this.lastSeenServerSeq,
          subscriptions: reconnectChannels,
        })
        resumedConnection = true
      } catch (error) {
        if (!(error instanceof RpcError) || error.code !== AhpErrorCodes.NotFound) throw error
        this.telemetryCapabilities = undefined
        handshake = await client.initialize({
          clientId: mc.client_id,
          protocolVersions: [...CLIENT_SUPPORTED_AHP_VERSIONS],
          initialSubscriptions: reconnectChannels,
        })
      }
    } else {
      this.telemetryCapabilities = undefined
      handshake = await client.initialize({
        clientId: mc.client_id,
        protocolVersions: [...CLIENT_SUPPORTED_AHP_VERSIONS],
        initialSubscriptions: [ROOT_CHANNEL],
      })
    }
    if (gen !== this.connectGen || this.disposed) {
      await closeSubscriptions(localSubscriptions.values())
      return
    }
    this.keepAliveTimeoutMs = readKeepAliveTimeoutMs(handshake)
    // The handshake round trip is itself proof the lane reaches the host, and
    // starts the keep-alive clock against the window it just advertised.
    this.markConfirmed()
    this.setPassive(isPassiveHandshakeResult(handshake))
    if ('telemetry' in handshake) this.telemetryCapabilities = handshake.telemetry
    const handshakeSnapshots = 'snapshots' in handshake ? handshake.snapshots : undefined
    const reconnectMissing =
      resumedConnection && 'missing' in handshake
        ? new Set(handshake.missing)
        : resumedConnection && handshakeSnapshots
          ? new Set(
              reconnectChannels.filter(channel => !handshakeSnapshots.some(snapshot => snapshot.resource === channel)),
            )
          : reconnectClientId !== undefined
            ? new Set(reconnectChannels.filter(channel => channel !== ROOT_CHANNEL))
            : undefined
    if (reconnectMissing?.has(ROOT_CHANNEL)) {
      await closeSubscriptions(localSubscriptions.values())
      throw new Error('AHP reconnect could not resume the root subscription')
    }
    // The session catalogue belongs to the connection that fetched it, so drop
    // it before any replayed action is applied. Otherwise a stale set both
    // suppresses the `root/activeSessionsChanged` fallback in
    // `applyReceivedAction` and keeps the pre-disconnect count on screen when
    // the next `listSessions` fails.
    this.sessionResources = undefined
    if (!resumedConnection) this.lastSeenServerSeq = 0
    if ('serverSeq' in handshake) {
      this.lastSeenServerSeq = Math.max(this.lastSeenServerSeq, handshake.serverSeq ?? 0)
    } else if ('actions' in handshake) {
      for (const envelope of handshake.actions) {
        this.applyReceivedAction(envelope, gen)
      }
    } else {
      for (const snapshot of handshakeSnapshots ?? []) {
        this.lastSeenServerSeq = Math.max(this.lastSeenServerSeq, snapshot.fromSeq ?? 0)
      }
    }
    const rootSnapshot = handshakeSnapshots?.find(snapshot => snapshot.resource === ROOT_CHANNEL)
    const isReplayReconnect = 'actions' in handshake
    if (rootSnapshot) {
      this.rootState = rootSnapshot.state as RootState
    } else if (!isReplayReconnect) {
      this.rootState = undefined
    }
    const authenticationSnapshots =
      isReplayReconnect && this.rootState ? [{resource: ROOT_CHANNEL, state: this.rootState}] : handshakeSnapshots
    const authentication = createAwpsAuthenticationContext(handshake, authenticationSnapshots, [mc.host_encryption_key])
    if (!resumedConnection) {
      this.emitHostInfo(handshake as InitializeResult)
    } else if (typeof this.rootState?.activeSessions === 'number') {
      // `emitHostInfo` is limited to a fresh `initialize` because `serverInfo`
      // and the negotiated protocol version only arrive there. The session
      // count can still move across a reconnect, so refresh just that fallback
      // from the resumed root snapshot; `listSessions` overwrites it moments
      // later on hosts that support the catalogue.
      this.activeSessions = this.rootState.activeSessions
      this.publishHostInfo()
    }
    // A supplied provider (e.g. chat's sealed data-token) wins for resources it
    // can satisfy; otherwise fall back to MC's resealed identity bearer.
    const optionProvider = this.options.resourceTokenProvider
    // The identity bearer is only offered to GitHub hosts (the client decides
    // GitHub-ness, not the daemon). v0.5.3+ routes the `authenticate` decrypt by
    // resource and rejects an `auth-token` on an unadvertised resource (`-32011`).
    const mcTokenProvider = sealedGitHubTokenProvider(mc.encrypted_github_token, defaultResourceTokenProvider)
    const resourceTokenProvider = optionProvider
      ? withFallbackResourceTokenProvider(optionProvider, mcTokenProvider)
      : mcTokenProvider
    this.resourceTokenProvider = resourceTokenProvider
    this.authentication = authentication
    this.setConnectionProgress({phase: 'authenticating'})
    await this.authenticateAdvertisedResources(client, authenticationSnapshots, resourceTokenProvider, authentication)
    if (gen !== this.connectGen || this.disposed) {
      await closeSubscriptions(localSubscriptions.values())
      return
    }
    const rootSubscription = takeSubscription(localSubscriptions, ROOT_CHANNEL)
    if (!rootSubscription) throw new Error('relay root subscription was not attached')

    // AHP 0.4.0 split the conversation off the session: turns/tool calls now
    // live on a chat channel. Subscribe to the session only to discover its
    // `defaultChat`, then observe (and dispatch onto) that chat. If the session
    // snapshot does not advertise a default chat, fail rather than inventing a
    // URI: AHP 0.9 chat channels are opaque and do not encode their session.
    // Create the daemon session then subscribe; a managed-sandbox observe instead
    // attaches first and creates only on `-32008` (see attachOrCreateSession).
    // Passive connections are read-only, so attachOrCreateSession observes only
    // and never sends createSession.
    const resumedSessionSnapshot = handshakeSnapshots?.find(snapshot => snapshot.resource === this.observedChannel())
    const resumedSessionSubscription = takeSubscription(localSubscriptions, this.observedChannel())
    if (resumedSessionSubscription && reconnectMissing?.has(this.observedChannel())) {
      await resumedSessionSubscription.close()
    }
    this.setConnectionProgress({phase: 'restoring-session'})
    const {result: sessionResult, subscription: sessionSubscription} =
      resumedSessionSubscription && !reconnectMissing?.has(this.observedChannel())
        ? {result: {snapshot: resumedSessionSnapshot}, subscription: resumedSessionSubscription}
        : await this.attachOrCreateSession(client, resourceTokenProvider, authenticationSnapshots, authentication)
    if (gen !== this.connectGen || this.disposed) {
      await sessionSubscription.close()
      await closeSubscriptions(localSubscriptions.values())
      return
    }
    if (sessionResult.snapshot) {
      this.lastSeenServerSeq = Math.max(this.lastSeenServerSeq, sessionResult.snapshot.fromSeq ?? 0)
      if (isSessionState(sessionResult.snapshot.state)) {
        this.sessionState = sessionResult.snapshot.state
        this.options.onSessionState?.(this.sessionState)
        await this.authenticatePendingTools(client, gen)
      }
    }
    const chat =
      defaultChatChannel(sessionResult.snapshot?.state) ??
      (isReplayReconnect ? (defaultChatChannel(this.sessionState) ?? this.chatChannel) : undefined)
    if (!chat) {
      await closeSubscriptions([rootSubscription, sessionSubscription, ...localSubscriptions.values()])
      throw new Error('AHP session advertised no chat channel')
    }
    void this.seedAndConsumeRoot(client, rootSubscription, gen)
    this.chatChannel = chat
    // Re-assert the active-client role (and our contributed tools) on every
    // establish while we still hold the session subscription. `createSession`
    // registers the tools at create time, but the host releases the
    // *active-client role* when a connection is finalized without a
    // same-`clientId` reconnect (e.g. a full `/connect` that mints a new
    // `clientId`, or a grace-window expiry). Without this re-claim the tool
    // stays callable but the host has no client to route the call to, so it
    // stalls until the host times it out. Best-effort: a rejected claim must not
    // fail the observe connection.
    this.advertiseActiveClient(client, this.instructionPlugin.hasPublishedCustomization())
    // Keep the session subscription open as a provisional copilotd
    // workaround. The host tracks "last AHP subscriber" by the session URI and
    // drains pending interactive prompts when that count reaches zero, even when
    // the client remains subscribed to the session's default chat URI. Once
    // copilotd treats the chat subscription as keeping the session interactive,
    // this keepalive can go back to an unsubscribe.
    void this.consumeSessionKeepalive(sessionSubscription, gen)

    const resumedChatSnapshot = handshakeSnapshots?.find(snapshot => snapshot.resource === chat)
    const resumedChatSubscription = takeSubscription(localSubscriptions, chat)
    if (resumedChatSubscription && reconnectMissing?.has(chat)) {
      await resumedChatSubscription.close()
    }
    this.setConnectionProgress({phase: 'restoring-conversation'})
    const {result, subscription} =
      resumedChatSubscription && !reconnectMissing?.has(chat)
        ? {result: {snapshot: resumedChatSnapshot}, subscription: resumedChatSubscription}
        : await this.subscribeWithAuth(client, chat, resourceTokenProvider, authentication)
    if (gen !== this.connectGen || this.disposed) {
      await subscription.close()
      return
    }
    if (result.snapshot) {
      this.lastSeenServerSeq = Math.max(this.lastSeenServerSeq, result.snapshot.fromSeq ?? 0)
      if (isChatState(result.snapshot.state)) {
        this.activeTurnId = result.snapshot.state.activeTurn?.id
        this.options.onSnapshot(result.snapshot.state)
      }
    }
    for (const [channel, observed] of this.observedChats) {
      const resumedSnapshot = handshakeSnapshots?.find(snapshot => snapshot.resource === channel)
      const resumedSubscription = takeSubscription(localSubscriptions, channel)
      if (resumedSubscription && reconnectMissing?.has(channel)) {
        await resumedSubscription.close()
      }
      await this.startObservedChat(
        channel,
        observed,
        gen,
        resumedSubscription && !reconnectMissing?.has(channel)
          ? {result: {snapshot: resumedSnapshot}, subscription: resumedSubscription}
          : undefined,
      )
    }
    for (const [channel, observed] of this.observedChangesets) {
      const resumedSnapshot = handshakeSnapshots?.find(snapshot => snapshot.resource === channel)
      const resumedSubscription = takeSubscription(localSubscriptions, channel)
      if (resumedSubscription && reconnectMissing?.has(channel)) {
        await resumedSubscription.close()
      }
      await this.startObservedChangeset(
        channel,
        observed,
        gen,
        resumedSubscription && !reconnectMissing?.has(channel)
          ? {result: {snapshot: resumedSnapshot}, subscription: resumedSubscription}
          : undefined,
      )
    }
    if (this.telemetryObservers.size > 0) {
      try {
        await this.startTelemetrySubscriptions(client)
      } catch (error) {
        const message = formatError(error)
        for (const observer of this.telemetryObservers) observer.onError?.(message)
      }
    }
    await closeSubscriptions(localSubscriptions.values())
    if (gen !== this.connectGen || this.disposed) return
    this.reconnectAttempts = 0
    this.reconnectScheduledForGen = undefined
    this.clientEverConnected = true
    this.setConnectionProgress({phase: 'connected'})
    this.setStatus('connected')
    void this.consume(subscription, gen)
  }

  private async createDaemonSession(
    client: AhpClient,
    provider: ResourceTokenProvider,
    snapshots: Array<{resource: URI; state: unknown}> | undefined,
    authentication: AwpsAuthenticationContext,
  ): Promise<void> {
    const opts = this.options.createSession
    if (!opts) return

    let workingDirectory = opts.workingDirectory
    let config: Record<string, unknown> | undefined

    if (opts.project) {
      const resolved = await this.provisionProject(
        client,
        provider,
        authentication,
        this.rootStateFrom(snapshots),
        opts.project,
      )
      workingDirectory = resolved.path
      const baseBranch = opts.project.baseBranch ?? resolved.defaultBranch
      // Without a base branch we must not fall back to the project's shared main
      // checkout (a mutable, shared working dir) — fail provisioning instead.
      if (!baseBranch) {
        throw new ProjectProvisioningError(
          'clone-failed',
          'Could not determine a base branch for the repository worktree.',
        )
      }
      config = {target: SESSION_CONFIG_TARGET_WORKTREE, baseBranch}
    }

    if (opts.enableSessionStore) {
      config = {...config, enableSessionStore: true}
    }

    // `config.systemMessageSections` reached copilotd in v0.7.2. Sent
    // unconditionally because an older host ignores config keys it does not
    // know (`check_unsupported_create_session_fields` validates only the keys
    // it owns), so the worst case is a session without the scope preamble
    // rather than a failed create. If we ever run a mixed fleet where that
    // silent loss matters, feature-detect the property's presence in the
    // schema returned by `resolveSessionConfig`, as the host spec prescribes.
    const scopeSections = buildSessionScopeSections(opts.sessionScope)
    const promptSections = mergeSystemMessageSections(
      scopeSections,
      buildPlaywrightLocalhostSections(opts.managedPlaywrightMcp),
      buildExplainErrorSections(opts.explainError),
    )
    if (promptSections) {
      config = {...config, systemMessageSections: promptSections}
    }

    const activeClient = this.buildActiveClient()
    const params: CreateSessionParams = {
      channel: sessionChannel(this.options.sessionId),
      ...(workingDirectory ? {workingDirectories: [toWorkingDirectoryUri(workingDirectory)]} : {}),
      ...(config ? {config} : {}),
      ...(opts.model ? {model: opts.model} : {}),
      ...(activeClient ? {activeClient} : {}),
    }
    await createSessionWithAuth(client, params, provider, authentication)
  }

  private rootStateFrom(snapshots: Array<{resource: URI; state: unknown}> | undefined): RootState | undefined {
    const rootSnapshot = snapshots?.find(snapshot => snapshot.resource === ROOT_CHANNEL)
    return isRootState(rootSnapshot?.state) ? rootSnapshot.state : undefined
  }

  private emitHostInfo(init: InitializeResult): void {
    if (!this.options.onHostInfo) return
    const root = this.rootStateFrom(init.snapshots)
    const serverName = nonEmptyString(init.serverInfo?.name)
    const serverVersion = nonEmptyString(init.serverInfo?.version)
    const serverTitle = nonEmptyString(init.serverInfo?.title)
    const hostName = readHostName(root)
    this.hostInfoBase = {
      ...(serverName ? {serverName} : {}),
      ...(serverVersion ? {serverVersion} : {}),
      ...(serverTitle ? {serverTitle} : {}),
      ...(nonEmptyString(init.protocolVersion) ? {protocolVersion: init.protocolVersion} : {}),
      ...(nonEmptyString(init.defaultDirectory) ? {defaultDirectory: init.defaultDirectory} : {}),
      ...(hostName ? {hostName} : {}),
    }
    if (typeof root?.activeSessions === 'number') this.activeSessions = root.activeSessions
    this.publishHostInfo()
  }

  private publishHostInfo(): void {
    if (!this.options.onHostInfo || !this.hostInfoBase) return
    this.options.onHostInfo({
      ...this.hostInfoBase,
      ...(typeof this.activeSessions === 'number' ? {activeSessions: this.activeSessions} : {}),
    })
  }

  /**
   * Resolve a repository to a launchable working directory (catalogue match or
   * clone), surfacing progress via `onProvisioning`. Throws {@link ProjectProvisioningError}.
   */
  private provisionProject(
    client: AhpClient,
    provider: ResourceTokenProvider,
    authentication: AwpsAuthenticationContext,
    rootState: RootState | undefined,
    project: RelayProjectProvisioning,
  ): Promise<ResolvedProject> {
    return resolveProjectWorkingDirectory({
      client,
      rootState,
      cloneUrl: project.cloneUrl,
      // ROOT_CHANNEL is already subscribed, so attach a local consumer for clone-progress deltas.
      watchRoot: () => client.attachSubscription(ROOT_CHANNEL),
      authenticate: async () => {
        // Prefer the daemon-advertised resources (host-correct), else the default
        // GitHub API resource; `provider` gates which actually receive a token.
        // Best-effort: public clones proceed without one.
        const advertised = advertisedProtectedResourceUris(rootState)
        const resources = advertised.length > 0 ? advertised : [GITHUB_API_RESOURCE]
        await satisfyResources(
          client,
          resources.map(resource => ({resource, required: true})),
          provider,
          authentication,
        )
      },
      onProgress: status => this.options.onProvisioning?.(status),
    })
  }

  private mcConnectDefault(signal: AbortSignal): Promise<MCConnectResponse> {
    if (!this.options.fetcher) throw new Error('relay connection runtime requires a fetcher or mcConnect')
    return connectToEnvironment(this.options.environmentId, this.options.sessionId, this.options.fetcher, {
      signal,
      onProgress: progress => this.setConnectionProgress(progress),
    })
  }

  private mcReconnectDefault(clientId: string, signal: AbortSignal): Promise<MCConnectResponse> {
    if (!this.options.fetcher) throw new Error('relay connection runtime requires a fetcher or mcReconnect')
    return reconnectToEnvironment(this.options.environmentId, clientId, this.options.fetcher, {signal})
  }

  private async authenticateAdvertisedResources(
    client: AhpClient,
    snapshots: Array<{resource: URI; state: unknown}> | undefined,
    provider: ResourceTokenProvider,
    authentication: AwpsAuthenticationContext,
  ): Promise<void> {
    const rootSnapshot = snapshots?.find(snapshot => snapshot.resource === ROOT_CHANNEL)
    if (!isRootState(rootSnapshot?.state)) return

    const resources = rootSnapshot.state.agents.flatMap(agent => agent.protectedResources ?? [])
    await satisfyResources(client, resources, provider, authentication)
  }

  /**
   * Establish the session subscription. Default: create the daemon session then
   * subscribe (the create page knows the session is new). When
   * `createSession.attachOrCreate` is set, subscribe first and create only on a
   * genuine `-32008`, so a hard-navigated observe attaches to an existing
   * session instead of re-creating it (which the daemon rejects).
   */
  private async attachOrCreateSession(
    client: AhpClient,
    provider: ResourceTokenProvider,
    snapshots: Array<{resource: URI; state: unknown}> | undefined,
    authentication: AwpsAuthenticationContext,
  ): Promise<Awaited<ReturnType<AhpClient['subscribe']>>> {
    const opts = this.options.createSession
    if (!opts || this.sessionCreated || this.passive) {
      return this.subscribeWithAuth(
        client,
        this.observedChannel(),
        provider,
        authentication,
        classifySessionSubscriptionError,
      )
    }
    if (!opts.attachOrCreate) {
      await this.createDaemonSession(client, provider, snapshots, authentication)
      this.sessionCreated = true
      return this.subscribeWithAuth(client, this.observedChannel(), provider, authentication)
    }
    try {
      const attached = await this.subscribeWithAuth(client, this.observedChannel(), provider, authentication)
      this.sessionCreated = true
      return attached
    } catch (err) {
      if (!(err instanceof RpcError) || err.code !== SESSION_NOT_FOUND_CODE) throw err
    }
    await this.createDaemonSession(client, provider, snapshots, authentication)
    this.sessionCreated = true
    return this.subscribeWithAuth(client, this.observedChannel(), provider, authentication)
  }

  private async subscribeWithAuth(
    client: AhpClient,
    channel: URI,
    provider: ResourceTokenProvider,
    authentication: AwpsAuthenticationContext,
    classifySubscribeError?: (error: unknown) => unknown,
  ): ReturnType<AhpClient['subscribe']> {
    let attempt = 0
    for (;;) {
      try {
        return await client.subscribe(channel)
      } catch (err) {
        if (!(err instanceof RpcError) || err.code !== AUTH_REQUIRED_CODE || attempt >= MAX_AUTH_RETRIES) {
          throw classifySubscribeError ? classifySubscribeError(err) : err
        }
        const satisfied = await satisfyResources(client, authRequiredResources(err.data), provider, authentication)
        if (!satisfied) throw err
        attempt++
      }
    }
  }

  private async startObservedChat(
    channel: URI,
    observed: ObservedChat,
    gen: number,
    resumed?: Awaited<ReturnType<AhpClient['subscribe']>>,
  ): Promise<void> {
    const client = this.client
    const provider = this.resourceTokenProvider
    const authentication = this.authentication
    if (!client || !provider || !authentication) {
      observed.observer.onError?.('Relay connection is not ready.')
      return
    }

    try {
      const {result, subscription} =
        resumed ?? (await this.subscribeWithAuth(client, channel, provider, authentication))
      if (this.observedChats.get(channel) !== observed || gen !== this.connectGen || this.disposed) {
        await subscription.close()
        if (!this.observedChats.has(channel) && client === this.client) await client.unsubscribe(channel)
        return
      }

      observed.subscription = subscription
      if (result.snapshot) {
        this.lastSeenServerSeq = Math.max(this.lastSeenServerSeq, result.snapshot.fromSeq ?? 0)
        if (isChatState(result.snapshot.state)) observed.observer.onSnapshot(result.snapshot.state)
      }
      void this.consumeObservedChat(channel, observed, subscription, gen)
    } catch (err) {
      if (this.observedChats.get(channel) === observed && gen === this.connectGen && !this.disposed) {
        observed.observer.onError?.(formatError(err))
      }
    }
  }

  private async startObservedChangeset(
    channel: URI,
    observed: ObservedChangeset,
    gen: number,
    resumed?: Awaited<ReturnType<AhpClient['subscribe']>>,
  ): Promise<void> {
    const client = this.client
    const provider = this.resourceTokenProvider
    const authentication = this.authentication
    if (!client || !provider || !authentication) {
      observed.observer.onError?.('Relay connection is not ready.')
      return
    }

    try {
      const {result, subscription} =
        resumed ?? (await this.subscribeWithAuth(client, channel, provider, authentication))
      if (this.observedChangesets.get(channel) !== observed || gen !== this.connectGen || this.disposed) {
        await subscription.close()
        if (!this.observedChangesets.has(channel) && client === this.client) await client.unsubscribe(channel)
        return
      }

      observed.subscription = subscription
      if (result.snapshot) {
        this.lastSeenServerSeq = Math.max(this.lastSeenServerSeq, result.snapshot.fromSeq ?? 0)
        if (isChangesetState(result.snapshot.state)) observed.observer.onSnapshot(result.snapshot.state)
      }
      void this.consumeObservedChangeset(channel, observed, subscription, gen)
    } catch (err) {
      if (this.observedChangesets.get(channel) === observed && gen === this.connectGen && !this.disposed) {
        observed.observer.onError?.(formatError(err))
      }
    }
  }

  /**
   * Combined active-client publication covering both the caller-supplied
   * customization (`createSession.activeClient`, e.g. `displayName`) and the
   * current tool definitions from {@link tools}.
   *
   * Each `session/activeClientSet` on the session channel is a full replacement
   * rather than a merge — publishing only one source would strand the other,
   * leaving tool calls the agent may still emit with no fulfilment path. The
   * `clientId` MUST match the one passed to `initialize` (`mcClientId`) per the
   * AHP `createSession` contract; it is also what the host stamps on
   * `toolCallStart.contributor` when routing calls back to us.
   */
  private buildActiveClient(includeEmpty = false): SessionActiveClient | undefined {
    const clientId = this.mcClientId
    if (!clientId) return undefined
    return this.instructionPlugin.buildActiveClient({
      clientId,
      supplied: this.options.createSession?.activeClient?.(clientId),
      contributedTools: this.tools.map(tool => tool.definition),
      includeEmpty,
    })
  }

  /**
   * Re-claim the active-client role for the current `mcClientId` and re-publish
   * the combined active client. Idempotent and best-effort: on a
   * same-`clientId` reconnect this simply re-affirms the role the host already
   * kept; on a full `/connect` (new `clientId`) it hands the active-client role
   * to the new connection so the host routes tool calls here. A rejected claim
   * (e.g. another active client) is logged and never fails the observe
   * connection.
   *
   * No-op when there is nothing to advertise (no tools, no supplied
   * customization) — a toolless observer never publishes.
   */
  private advertiseActiveClient(client: AhpClient, includeEmpty = false): void {
    if (this.passive) return
    const activeClient = this.buildActiveClient(includeEmpty)
    if (!activeClient) return
    const action: StateAction = {
      type: ACTION_ACTIVE_CLIENT_SET,
      activeClient,
    }
    try {
      this.markOutbound()
      client.dispatch(this.observedChannel(), action)
      this.activeClientPublished = true
    } catch (err) {
      this.logger.error(`relay: re-asserting active client failed: ${formatError(err)}`)
    }
  }

  private async openTransport(mc: MCConnectResponse): Promise<AhpTransport> {
    const minted = parseGroupName(mc.groups.broadcast)
    return WpsTransport.connect({
      url: buildWpsUrl(mc),
      toHostGroup: mc.groups.to_host,
      joinGroups: [mc.groups.broadcast, mc.groups.to_client],
      groupValidation: {expected: {uid: minted.uid, eid: minted.eid, cid: mc.client_id}},
      onProtocolError: err => this.logger.error(`relay WPS frame dropped: ${formatError(err)}`),
      onMessage: message => this.handleTelemetryMessage(message),
      ...(this.options.webSocketFactory ? {webSocketFactory: this.options.webSocketFactory} : {}),
    })
  }

  private telemetryChannelEntries(): Array<[RelayTelemetrySignal, URI]> {
    const telemetry = this.telemetryCapabilities
    if (!telemetry) return []

    const advertised: Array<[RelayTelemetrySignal, URI | undefined]> = [
      ['logs', telemetry.logs],
      ['traces', telemetry.traces],
      ['metrics', telemetry.metrics],
    ]
    const result: Array<[RelayTelemetrySignal, URI]> = []
    for (const [signal, uri] of advertised) {
      if (!uri) continue
      let channel = uri
      if (signal === 'logs') {
        if (channel.includes('{?level}')) {
          channel = channel.replaceAll('{?level}', '')
        } else if (channel.includes('{level}')) {
          channel = channel.replaceAll('{level}', 'debug')
        }
      }
      if (channel.includes('{') || channel.includes('}')) continue
      result.push([signal, channel])
    }
    return result
  }

  private startTelemetrySubscriptions(client: AhpClient): Promise<void> {
    if (this.telemetryStartInFlight) return this.telemetryStartInFlight
    const run = this.runTelemetrySubscriptions(client)
    this.telemetryStartInFlight = run
    void this.clearTelemetryStartWhenSettled(run)
    return run
  }

  private async clearTelemetryStartWhenSettled(run: Promise<void>): Promise<void> {
    try {
      await run
    } catch {
      // Callers of startTelemetrySubscriptions observe the rejection; we only clear the slot here.
    }
    if (this.telemetryStartInFlight === run) this.telemetryStartInFlight = null
  }

  private async runTelemetrySubscriptions(client: AhpClient): Promise<void> {
    const entries = this.telemetryChannelEntries()
    if (entries.length === 0) throw new Error('The host does not advertise subscribable OTLP telemetry channels.')

    const added: URI[] = []
    try {
      for (const [, channel] of entries) {
        if (this.telemetryChannels.has(channel)) continue
        this.telemetryChannels.add(channel)
        try {
          const {subscription} = await client.subscribe(channel)
          await subscription.close()
        } catch (error) {
          // Subscribe failed: this channel is not live server-side, so don't unsubscribe it.
          this.telemetryChannels.delete(channel)
          throw error
        }
        added.push(channel)
      }
    } catch (error) {
      for (const channel of added) {
        this.telemetryChannels.delete(channel)
        try {
          await client.unsubscribe(channel)
        } catch {
          // Best-effort rollback; the transport may be tearing down.
        }
      }
      throw error
    }
  }

  private handleTelemetryMessage(message: JsonRpcMessage): void {
    if (this.telemetryObservers.size === 0) return
    if (!('method' in message)) return
    const signal =
      message.method === 'otlp/exportLogs'
        ? 'logs'
        : message.method === 'otlp/exportTraces'
          ? 'traces'
          : message.method === 'otlp/exportMetrics'
            ? 'metrics'
            : undefined
    if (!signal || !message.params || typeof message.params !== 'object') return

    const params = message.params as {channel?: unknown; payload?: unknown}
    if (
      typeof params.channel !== 'string' ||
      !this.telemetryChannels.has(params.channel) ||
      !params.payload ||
      typeof params.payload !== 'object' ||
      Array.isArray(params.payload)
    ) {
      return
    }

    const event: RelayTelemetryEvent = {
      signal,
      channel: params.channel,
      payload: params.payload as Record<string, unknown>,
    }
    for (const observer of this.telemetryObservers) {
      try {
        observer.onEvent(event)
      } catch (error) {
        try {
          observer.onError?.(`Telemetry observer failed: ${formatError(error)}`)
        } catch {
          // Keep diagnostics observers from interrupting the relay receive loop.
        }
      }
    }
  }

  /** Track the active turn id: `turnStarted` sets it, terminal actions clear it. */
  private trackActiveTurn(action: StateAction): void {
    if (action.type === ACTION_TURN_STARTED) {
      this.activeTurnId = action.turnId
      // Not cleared on turn end (see field), so a later `_meta` failure still maps here.
      this.lastTurnId = action.turnId
      return
    }

    const endsTurn =
      action.type === ACTION_TURN_CANCELLED || action.type === ACTION_TURN_COMPLETE || action.type === ACTION_TURN_ERROR
    if (endsTurn && 'turnId' in action && action.turnId === this.activeTurnId) {
      this.activeTurnId = undefined
    }
  }

  private async authenticatePendingTools(client: AhpClient, gen: number): Promise<void> {
    const provider = this.resourceTokenProvider
    const authentication = this.authentication
    if (!provider || !authentication) return

    const requests =
      this.sessionState?.inputNeeded?.filter(
        (request): request is SessionToolAuthenticationRequest =>
          request.kind === SESSION_INPUT_KIND_TOOL_AUTHENTICATION,
      ) ?? []
    for (const request of requests) {
      const inFlightKey = `${gen}:${request.id}`
      if (this.toolAuthenticationInFlight.has(inFlightKey)) continue
      this.toolAuthenticationInFlight.add(inFlightKey)
      try {
        const {resource, requiredScopes} = request.toolCall.auth
        await satisfyResources(
          client,
          [{...resource, required: true, scopes_supported: requiredScopes ?? resource.scopes_supported}],
          provider,
          authentication,
        )
      } finally {
        this.toolAuthenticationInFlight.delete(inFlightKey)
      }
    }
  }

  private async authenticatePendingToolsSafely(client: AhpClient, gen: number): Promise<void> {
    try {
      await this.authenticatePendingTools(client, gen)
    } catch (err) {
      this.logger.error(`relay tool authentication failed: ${formatError(err)}`)
    }
  }

  /**
   * Surface a model-call failure copilotd reports only on the session `_meta`
   * side-channel (not as a `chat/error`), attributed to {@link lastTurnId}. The
   * host never clears it and re-emits `_meta` on any change, so dedupe by
   * `apiCallId` to surface each failure once.
   */
  private handleSessionMetaFailure(action: StateAction): void {
    if (action.type !== ACTION_SESSION_META_CHANGED) return
    const meta = (action as {_meta?: Record<string, unknown> | undefined})._meta ?? undefined
    const raw = meta?.[META_KEY_MODEL_CALL_FAILURE]
    if (!raw || typeof raw !== 'object') return
    const failure = normalizeModelCallFailure(meta)
    if (!failure || !this.lastTurnId) return
    const rawFailure = raw as Record<string, unknown>
    const id =
      nonEmptyString(rawFailure['apiCallId']) ??
      nonEmptyString(rawFailure['serviceRequestId']) ??
      JSON.stringify(failure)
    if (this.seenModelFailureIds.has(id)) return
    this.seenModelFailureIds.add(id)
    this.options.onModelCallFailure?.(failure, this.lastTurnId)
  }

  private applyReceivedAction(envelope: ActionEnvelope, gen: number, sourceChannel = envelope.channel): void {
    this.settlePendingSessionDispatch(envelope, sourceChannel)
    if (sourceChannel === ROOT_CHANNEL) {
      if (this.rootState) this.rootState = rootReducer(this.rootState, envelope.action as RootAction)
      if (
        !this.sessionResources &&
        envelope.action.type === ACTION_ROOT_ACTIVE_SESSIONS_CHANGED &&
        envelope.action.activeSessions !== this.activeSessions
      ) {
        this.activeSessions = envelope.action.activeSessions
        this.publishHostInfo()
      }
    } else if (sourceChannel === this.observedChannel()) {
      if (!envelope.rejectionReason && this.sessionState) {
        this.sessionState = sessionReducer(this.sessionState, normalizeSessionAction(envelope.action as SessionAction))
        this.options.onSessionState?.(this.sessionState)
        const client = this.client
        if (client) {
          void this.authenticatePendingToolsSafely(client, gen)
        }
      }
      if (!envelope.rejectionReason) this.handleSessionMetaFailure(envelope.action)
      try {
        this.options.onAction(envelope)
      } catch (err) {
        this.logger.error(`relay action listener failed: ${formatError(err)}`)
      }
    } else if (sourceChannel === this.chatChannel) {
      this.trackActiveTurn(envelope.action)
      try {
        this.options.onAction(envelope)
      } catch (err) {
        this.logger.error(`relay action listener failed: ${formatError(err)}`)
      }
      this.handleClientToolAction(envelope, gen)
    } else {
      const observedChat = this.observedChats.get(sourceChannel)
      if (observedChat) {
        try {
          observedChat.observer.onAction(envelope)
        } catch (err) {
          this.logger.error(`relay observed-chat listener failed: ${formatError(err)}`)
        }
      }
      const observedChangeset = this.observedChangesets.get(sourceChannel)
      if (observedChangeset) {
        try {
          observedChangeset.observer.onAction(envelope)
        } catch (err) {
          this.logger.error(`relay observed-changeset listener failed: ${formatError(err)}`)
        }
      }
    }
    this.lastSeenServerSeq = Math.max(this.lastSeenServerSeq, envelope.serverSeq ?? 0)
  }

  private async consume(subscription: Subscription, gen: number): Promise<void> {
    try {
      for await (const evt of subscription) {
        if (this.disposed || gen !== this.connectGen) return
        if (evt.type !== 'action') continue
        this.applyReceivedAction(evt.params, gen, this.chatChannel)
      }
    } catch (err) {
      if (gen === this.connectGen && !this.disposed) {
        this.logger.error(`relay subscription ended: ${formatError(err)}`)
      }
    }
    if (gen === this.connectGen && !this.disposed) {
      this.scheduleReconnect(gen)
    }
  }

  private async consumeObservedChat(
    channel: URI,
    observed: ObservedChat,
    subscription: Subscription,
    gen: number,
  ): Promise<void> {
    try {
      for await (const evt of subscription) {
        if (this.disposed || gen !== this.connectGen || this.observedChats.get(channel) !== observed) return
        if (evt.type === 'action') this.applyReceivedAction(evt.params, gen, channel)
      }
    } catch (err) {
      if (gen === this.connectGen && !this.disposed && this.observedChats.get(channel) === observed) {
        observed.observer.onError?.(formatError(err))
      }
      return
    }
    if (gen === this.connectGen && !this.disposed && this.observedChats.get(channel) === observed) {
      observed.observer.onError?.('Chat subscription ended.')
    }
  }

  private async consumeObservedChangeset(
    channel: URI,
    observed: ObservedChangeset,
    subscription: Subscription,
    gen: number,
  ): Promise<void> {
    try {
      for await (const evt of subscription) {
        if (this.disposed || gen !== this.connectGen || this.observedChangesets.get(channel) !== observed) return
        if (evt.type === 'action') this.applyReceivedAction(evt.params, gen, channel)
      }
    } catch (err) {
      if (gen === this.connectGen && !this.disposed && this.observedChangesets.get(channel) === observed) {
        observed.observer.onError?.(formatError(err))
      }
      return
    }
    if (gen === this.connectGen && !this.disposed && this.observedChangesets.get(channel) === observed) {
      observed.observer.onError?.('Changeset subscription ended.')
    }
  }

  private async consumeSessionKeepalive(subscription: Subscription, gen: number): Promise<void> {
    try {
      for await (const evt of subscription) {
        if (this.disposed || gen !== this.connectGen) return
        if (evt.type === 'action') {
          this.applyReceivedAction(evt.params, gen, this.observedChannel())
        }
      }
    } catch (err) {
      if (gen === this.connectGen && !this.disposed) {
        this.logger.error(`relay session subscription ended: ${formatError(err)}`)
      }
    }

    // Session-scoped actions (like `session/titleChanged` from client tool
    // completions) ride this stream, so a silent exit would leave those
    // actions undeliverable while the chat subscription may still look healthy.
    // Treat termination as a reconnect condition; the dedup in
    // `scheduleReconnect` collapses this into the chat stream's schedule when
    // both drop together.
    if (gen === this.connectGen && !this.disposed) {
      this.scheduleReconnect(gen)
    }
  }

  private async seedAndConsumeRoot(client: AhpClient, subscription: Subscription, gen: number): Promise<void> {
    if (this.options.onHostInfo) {
      try {
        const resources = new Set<URI>()
        let cursor: string | undefined
        do {
          const result = await client.request('listSessions', {
            channel: ROOT_CHANNEL,
            ...(cursor ? {cursor} : {}),
          })
          if (this.disposed || gen !== this.connectGen) return
          for (const session of result.items) resources.add(session.resource)
          cursor = result.nextCursor
        } while (cursor)
        this.sessionResources = resources
        this.activeSessions = resources.size
        this.publishHostInfo()
      } catch (err) {
        // Hosts predating the catalogue answer `-32601`. That is the documented
        // fallback to `root/activeSessionsChanged`, not a failure, so it must
        // not raise an error on every connect and reconnect.
        if (err instanceof RpcError && err.code === JsonRpcErrorCodes.MethodNotFound) {
          this.logger.info('relay listSessions unsupported; falling back to root activeSessions deltas')
        } else {
          this.logger.error(`relay listSessions failed: ${formatError(err)}`)
        }
      }
    }

    try {
      for await (const evt of subscription) {
        if (this.disposed || gen !== this.connectGen) return
        if (evt.type === 'sessionAdded') {
          if (!this.sessionResources) continue
          const previousSize = this.sessionResources.size
          this.sessionResources.add(evt.params.summary.resource)
          if (this.sessionResources.size !== previousSize) {
            this.activeSessions = this.sessionResources.size
            this.publishHostInfo()
          }
        } else if (evt.type === 'sessionRemoved') {
          if (this.sessionResources?.delete(evt.params.session)) {
            this.activeSessions = this.sessionResources.size
            this.publishHostInfo()
          }
        } else if (evt.type === 'action') {
          this.applyReceivedAction(evt.params, gen, ROOT_CHANNEL)
        }
      }
    } catch (err) {
      if (gen === this.connectGen && !this.disposed) {
        this.logger.error(`relay root subscription ended: ${formatError(err)}`)
      }
    }
    if (gen === this.connectGen && !this.disposed) {
      this.scheduleReconnect(gen)
    }
  }

  private scheduleReconnect(gen: number): void {
    if (this.disposed || gen !== this.connectGen || this.terminalState.isTerminated()) return
    if (this.reconnectScheduledForGen === gen) return
    this.reconnectScheduledForGen = gen
    const attempt = this.reconnectAttempts++
    const delay = Math.min(RECONNECT_BASE_DELAY_MS * 2 ** attempt, RECONNECT_MAX_DELAY_MS)
    this.setConnectionProgress({phase: 'retrying', retryAttempt: attempt + 1})
    this.setStatus('reconnecting')
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = undefined
      if (this.disposed || gen !== this.connectGen) return
      const reconnectGen = ++this.connectGen
      void this.reestablish(reconnectGen)
    }, delay)
  }

  private async reestablish(gen: number): Promise<void> {
    this.establishInFlight = true
    await this.teardownClient()
    if (gen !== this.connectGen || this.disposed) {
      this.settleEstablish(gen)
      return
    }
    const signal = this.beginConnectAttempt()
    try {
      await this.establish(gen, true, signal)
    } catch (err) {
      if (gen !== this.connectGen || this.disposed) return
      if (await this.terminalState.handleTerminalEstablishError(err)) return
      // Failed to (re)establish; escalate the next attempt to a full `/connect`
      // (new client id + spawn) rather than reconnecting the same dead client.
      this.clientEverConnected = false
      // A mid-session reconnect failure is transient and self-healing (the next
      // backoff attempt recovers), so log it at info — not error. This keeps a
      // relay that is reconnecting in its dispose-grace window from tripping
      // `vitest-fail-on-console` (which fails only on console.error/warn) in an
      // unrelated test. A first-establish failure stays error-level in connect().
      this.logger.info(`relay reconnect failed: ${formatError(err)}`)
      this.scheduleReconnect(gen)
    } finally {
      this.settleEstablish(gen)
      this.finishConnectAttempt(signal)
    }
  }

  private beginConnectAttempt(): AbortSignal {
    this.abortConnectAttempt()
    const controller = new AbortController()
    this.connectAbortController = controller
    return controller.signal
  }

  private finishConnectAttempt(signal: AbortSignal): void {
    if (this.connectAbortController?.signal === signal) {
      this.connectAbortController = undefined
    }
  }

  private abortConnectAttempt(): void {
    if (this.reconnectTimer !== undefined) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = undefined
    }
    this.connectAbortController?.abort()
    this.connectAbortController = undefined
  }

  private async teardown(): Promise<void> {
    this.relinquishActiveClient()
    await this.teardownClient()
  }

  private relinquishActiveClient(): void {
    if (this.passive || !this.client || !this.mcClientId || !this.activeClientPublished) return
    try {
      this.markOutbound()
      this.client.dispatch(this.observedChannel(), {
        type: ACTION_SESSION_ACTIVE_CLIENT_REMOVED,
        clientId: this.mcClientId,
      })
    } catch (err) {
      this.logger.error(`relay: relinquishing active client failed: ${formatError(err)}`)
    }
  }

  private async teardownClient(): Promise<void> {
    const client = this.client
    this.client = null
    this.rejectPendingSessionDispatches(new Error('relay disconnected before the host accepted the session update'))
    this.stopKeepAlivePing()
    this.instructionPlugin.resetResourceClient()
    this.authentication = undefined
    this.resourceTokenProvider = undefined
    this.telemetryChannels.clear()
    for (const observed of this.observedChats.values()) observed.subscription = undefined
    for (const observed of this.observedChangesets.values()) observed.subscription = undefined
    this.activeClientPublished = false
    this.ownedToolCalls.clear()
    this.pendingClientToolReady.clear()
    if (client) {
      try {
        await client.shutdown()
      } catch {
        // best-effort
      }
    }
    const transport = this.transport
    this.transport = null
    if (transport) await safeClose(transport)
  }

  private setStatus(status: RelayStatus, error?: string, failure?: RelayFailure): void {
    this.status = status
    this.options.onStatus?.(status, error, failure)
  }

  private setConnectionProgress(progress: RelayConnectionProgress): void {
    if (
      this.connectionProgress?.phase === progress.phase &&
      this.connectionProgress.retryAttempt === progress.retryAttempt
    ) {
      return
    }
    this.connectionProgress = progress
    this.options.onConnectionProgress?.(progress)
  }

  private setPassive(passive: boolean): void {
    if (this.passiveKnown && passive === this.passive) return
    this.passiveKnown = true
    this.passive = passive
    this.options.onPassiveChange?.(passive)
  }

  /**
   * Drive the fulfilment half of a contributed client tool. The host attributes
   * a call to us on `toolCallStart` (via a client `contributor` carrying our
   * `clientId`) and streams the arguments; once it reaches `toolCallReady`
   * (auto-confirmed to `running` for client tools) we run the tool and report
   * the result with `chat/toolCallComplete`. We take ownership of every call
   * routed to our `clientId` — even for a tool we did not register — so a
   * mis-routed call is always completed (with a failure result) rather than
   * stalling the turn.
   */
  private handleClientToolAction(env: ActionEnvelope, gen: number): void {
    if (this.passive) return
    // Continue processing even when tools is empty: calls already owned (routed
    // before the last tool was unregistered) must still be completed so the
    // turn does not stall.
    if (this.tools.length === 0 && this.ownedToolCalls.size === 0) return
    const action = env.action as {
      type?: string
      toolCallId?: string
      turnId?: string
      toolName?: string
      toolInput?: ToolInput
      content?: string
      confirmed?: string
      contributor?: {kind?: string; clientId?: string}
    }
    if (!action.toolCallId) return

    if (action.type === ACTION_TOOL_CALL_START) {
      const owned = action.contributor?.kind === 'client' && action.contributor.clientId === this.mcClientId
      if (!owned) {
        // Routed to a different client: drop any ready we buffered for it so the
        // out-of-order buffer never retains calls that were never ours.
        this.pendingClientToolReady.delete(action.toolCallId)
        return
      }
      if (action.turnId && action.toolName) {
        const call: OwnedToolCall = {
          toolCallId: action.toolCallId,
          turnId: action.turnId,
          toolName: action.toolName,
          deltas: '',
        }
        this.ownedToolCalls.set(action.toolCallId, call)
        // A `toolCallReady` may have arrived before this start; now that the
        // call's turn/tool identity is known, replay it and complete the call.
        const pendingReady = this.pendingClientToolReady.get(action.toolCallId)
        if (pendingReady) {
          this.pendingClientToolReady.delete(action.toolCallId)
          this.processOwnedReady(call, pendingReady.toolInput, pendingReady.needsConfirmation, gen)
        }
      }
      return
    }

    if (action.type === ACTION_TOOL_CALL_DELTA) {
      const call = this.ownedToolCalls.get(action.toolCallId)
      if (call && action.content) call.deltas += action.content
      return
    }

    if (action.type === ACTION_TOOL_CALL_READY) {
      // The host leaves a client tool in `pending-confirmation` unless it
      // auto-confirms it (`confirmed` set, typically `'not-needed'`). When it
      // does not, we must approve the call ourselves — otherwise the host's
      // permission gate never opens and the turn stalls until the client-tool
      // timeout fires. The host then re-emits `toolCallReady` (now `running`,
      // carrying the resolved `toolInput`), which is where we complete the call.
      const needsConfirmation = action.confirmed == null
      const toolInput = inlineToolInput(action.toolInput)
      const call = this.ownedToolCalls.get(action.toolCallId)
      if (!call) {
        // Ready before start: buffer the input (ownership is unknown until an
        // owned start arrives) and act then, rather than dropping the event and
        // stalling the turn until the host times the call out.
        this.pendingClientToolReady.set(action.toolCallId, {
          ...(toolInput ? {toolInput} : {}),
          needsConfirmation,
        })
        return
      }
      this.processOwnedReady(call, toolInput, needsConfirmation, gen)
    }
  }

  /**
   * Act on a `toolCallReady` for a call we own. A call the host left in
   * `pending-confirmation` is approved and kept — we complete it only on the
   * follow-up `running` ready that carries the resolved `toolInput`. An
   * already-confirmed (or auto-confirmed) call is completed straight away.
   */
  private processOwnedReady(
    call: OwnedToolCall,
    toolInput: string | undefined,
    needsConfirmation: boolean,
    gen: number,
  ): void {
    if (needsConfirmation && !call.confirmed) {
      call.confirmed = true
      if (toolInput) call.readyInput = toolInput
      this.confirmOwnedToolCall(call, gen)
      return
    }
    this.ownedToolCalls.delete(call.toolCallId)
    this.runOwnedToolCall(call, toolInput ?? call.readyInput, gen)
  }

  /**
   * Fulfil an owned tool call and report its `chat/toolCallComplete`, preferring
   * the ready event's `toolInput` and falling back to the streamed deltas. By
   * this point any required confirmation has already been sent (see
   * {@link processOwnedReady}), so the call has reached `running`.
   */
  private runOwnedToolCall(call: OwnedToolCall, toolInput: string | undefined, gen: number): void {
    const resolvedInput = toolInput ?? (call.deltas || undefined)
    const withInput: ClientToolCall = {
      toolCallId: call.toolCallId,
      turnId: call.turnId,
      toolName: call.toolName,
      ...(resolvedInput ? {toolInput: resolvedInput} : {}),
    }
    const tool = this.tools.find(entry => entry.definition.name === call.toolName)
    void this.completeClientTool(tool, withInput, gen)
  }

  /**
   * Approve an owned client tool call the host left in `pending-confirmation`.
   * Client tools carry no user-facing permission UI here, so the runtime
   * auto-approves them (mirroring `confirmToolCall`'s approve branch) to open
   * the host's permission gate before dispatching the result.
   */
  private confirmOwnedToolCall(call: OwnedToolCall, gen: number): void {
    if (this.disposed || gen !== this.connectGen) return
    const client = this.client
    const chat = this.chatChannel
    if (!client || !chat) return
    const action: StateAction = {
      type: ACTION_TOOL_CALL_CONFIRMED,
      turnId: call.turnId,
      toolCallId: call.toolCallId,
      approved: true,
      confirmed: 'user-action',
    } as StateAction
    try {
      this.markOutbound()
      client.dispatch(chat, action)
    } catch (err) {
      this.logger.error(`relay: confirming client tool call failed: ${formatError(err)}`)
    }
  }

  private async completeClientTool(tool: ClientTool | undefined, call: ClientToolCall, gen: number): Promise<void> {
    let result: ToolCallResult
    if (!tool) {
      // The host routed a call to us for a tool we never registered. Complete it
      // with a failure so the host can surface the error and advance the turn.
      result = {
        success: false,
        pastTenseMessage: '',
        error: {message: `No client tool registered for "${call.toolName}"`},
      }
    } else {
      try {
        result = (await tool.fulfil?.(call)) ?? {success: true, pastTenseMessage: ''}
      } catch (err) {
        result = {success: false, pastTenseMessage: '', error: {message: formatError(err)}}
      }
    }
    if (this.disposed || gen !== this.connectGen) return
    const client = this.client
    const chat = this.chatChannel
    if (!client || !chat) return
    const action: StateAction = {
      type: ACTION_TOOL_CALL_COMPLETE,
      turnId: call.turnId,
      toolCallId: call.toolCallId,
      result,
    }
    try {
      this.markOutbound()
      client.dispatch(chat, action)
    } catch (err) {
      this.logger.error(`relay: completing client tool call failed: ${formatError(err)}`)
    }
  }
}

/**
 * Agent-level protected-resource URIs the daemon advertised — the resources to
 * attempt `authenticate` for before a private clone. The token provider gates
 * which actually receive the identity bearer, so non-GitHub ones are skipped.
 */
function advertisedProtectedResourceUris(rootState: RootState | undefined): string[] {
  if (!rootState) return []
  return rootState.agents.flatMap(agent => (agent.protectedResources ?? []).map(meta => meta.resource))
}

function takeSubscription(subscriptions: Map<URI, Subscription>, resource: URI): Subscription | undefined {
  const subscription = subscriptions.get(resource)
  subscriptions.delete(resource)
  return subscription
}

async function closeSubscriptions(subscriptions: Iterable<Subscription>): Promise<void> {
  await Promise.all([...subscriptions].map(async subscription => subscription.close()))
}

async function safeClose(transport: AhpTransport): Promise<void> {
  try {
    await transport.close()
  } catch {
    // best-effort
  }
}
